// components/slidesCommands.ts
//
// The Slides (Reviewer) agent's toolset - SLIDES_* commands operating on the
// deck open in the Slides tool. Same registry/runner mechanics as the design
// and dealer commands (see the developer note in designCommands.ts: these
// definitions are serialized verbatim into the system prompt). Handlers reach
// the module store directly (useSlidesStore.getState()), mirroring how the
// ANALYST_* commands drive tablesStore.
//
// Coordinates: PAGE POINTS, origin at the page's top-left (the page width and
// height in points are in the agent's live deck context).

import { SLIDES_RESEARCH_COMMANDS } from './slides/brandResearchCommands';
import { toast } from 'sonner';
import type {
  DeckReviewPointState,
  SlideDeckDTO,
  SlideElement,
  SlidePageDoc,
  SlideReviewVerdict,
  UiCommandDefinition,
} from '#types/types';
import { resolveAgentSlideDeck, slidesGenId, useSlidesStore } from './slides/slidesStore';
import { reviewHasCompleteResult, reviewProgress } from '#lib/slides/reviewStatus';
import {
  clampAnchorToPage,
  resolveAnchorText,
  reservedExportRegions,
  resolveNoteCollisions,
  MAX_TOLERABLE_COVERAGE,
  TYPICAL_NOTE,
  type Frame,
} from '#lib/slides/annotationLayout';
import { noteFrameForText } from '#lib/slides/noteMetrics';
import { buildNoteLayout } from '#lib/slides/noteLayout';
import {
  buildAppliedFromCroquisPrompt,
  buildCroquisPrompt,
  fitInBox,
} from '#lib/slides/counterProposal';
import {
  MAX_PROPOSAL_ATTEMPTS,
  refineChanges,
  type ProposalVerdict,
} from '#lib/slides/proposalCritic';
import { runStudioGeneration } from '#lib/studio/runStudioGeneration';
import { FLAGSHIP_STUDIO_MODEL_ID } from '#lib/model-registry';
import { fetchApi } from '@/kernel/apiFetch';
import { proposalSheetLayout } from '#lib/slides/proposalSheetLayout';
import { snapAnchorToInk, placeNoteInClearSpace, countFreeNoteSlots } from './slides/inkProbe';

/** Two callout dots closer than this are treated as the same spot. */
const ANCHOR_CLASH_PT = 12;
/** How far the de-clash nudge may move a dot from the element it was aimed at. */
const ANCHOR_DRIFT_MAX_PT = 48;

/** The JSON shape of `POST /api/slides/locate-anchor` (see `LocatedAnchor`).
 *  Restated rather than imported: the locator module pulls in sharp and the
 *  LangChain client, which must not follow this file into the browser bundle. */
interface LocatedAnchorResponse {
  x: number;
  y: number;
  whatIsThere: string;
  region: number;
  confidence: 'verified' | 'unverified' | 'lost';
  attempts: number;
  /** What this locate settled for, stamped on the placed element (credits). */
  credits?: number;
}

/** Accumulates a locate's settled price onto the element it anchors (re-anchoring
 *  pays again, so the amounts add up). The right bar's review-progress panel and
 *  the element inspector read `credits` back; the agent can never set it - the
 *  field is not in the command parser's allowlist. */
export const stampLocateCredits = (
  el: { credits?: number },
  located: LocatedAnchorResponse,
  prior?: number,
): void => {
  if (typeof located.credits === 'number' && Number.isFinite(located.credits)) {
    el.credits = (prior ?? el.credits ?? 0) + located.credits;
  }
};

/**
 * Resolve a NAMED target to an exact anchor, app-side (lib/slides/anchorLocator.ts):
 * the page is split into content panels, the element's panel is chosen, and the
 * point is read inside an enlarged crop of it. This is the reliable path on
 * graphic pages - asking the model to regress {x,y} over a whole A3 page put
 * 2 of 5 leader lines off their element on the real deck, one in the margin.
 * A numeric anchor is passed only as a tie-break; the region is always chosen by
 * asking, never inferred from the hint (see the note in anchorLocator).
 */
const locateAnchorForElement = async (
  page: SlidePageDoc,
  element: string,
  comment: string | undefined,
  hint: { x: number; y: number } | undefined,
  /** The deck's project, so the locator's credit spend is attributed to it. */
  projectId?: string
): Promise<LocatedAnchorResponse> => {
  if (!page.snapshotUrl) {
    throw new Error(
      `SLIDES_ADD_ELEMENT: page ${page.index + 1} has no snapshot, so anchorElement cannot be resolved. Use anchorText or a numeric anchor {x,y}.`
    );
  }
  // Transient failures are RETRIED here rather than handed to the agent. When
  // the agent sees an error it re-plans, and re-planning a placement means
  // inventing a coordinate - the one outcome this whole path exists to prevent.
  // A vision call over a page-sized image is long enough that an overloaded
  // model or a dropped socket is routine, so the retry is the difference
  // between "always works" and "usually works".
  let res: Response | null = null;
  let transport = '';
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt > 0) await new Promise((r) => setTimeout(r, 1200 * attempt));
    try {
      // fetchApi, NOT fetch - same reason as critiqueProposal below. A review
      // runs for minutes and calls this route once per callout, so it is the
      // request most likely to overlap a Clerk token refresh: the middleware
      // then answers the sign-in page wearing an HTTP 200, `res.ok` is true, the
      // loop breaks, and `res.json()` throws a parse error out of the handler -
      // aborting SLIDES_ADD_ELEMENT and discarding every callout the batch had
      // already located and paid for. fetchApi recognises the bounce and retries
      // the one request instead.
      res = await fetchApi('/api/slides/locate-anchor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assetPath: page.snapshotUrl,
          projectId,
          pageW: page.width,
          pageH: page.height,
          element,
          comment,
          hint: hint ?? null,
        }),
      });
    } catch (e) {
      // The request never completed; there is no status to reason about.
      transport = e instanceof Error ? e.message : String(e);
      res = null;
      continue;
    }
    // 4xx is a real answer about this input: retrying sends the identical body
    // and gets the identical reply, so only server-side faults are worth a
    // second attempt.
    if (res.ok || res.status < 500) break;
  }
  if (!res) {
    throw new Error(
      `SLIDES_ADD_ELEMENT: the anchor service was unreachable for "${element}" on page ${page.index + 1} after 3 attempts (${transport.slice(0, 120)}). Try the command again with the SAME anchorElement. Do not substitute a numeric anchor.`
    );
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    // A SERVER failure is not a bad description, and must not be reported as
    // one. This message used to end "or fall back to ... a numeric anchor" for
    // every status, so a 500 taught the agent to guess a coordinate - and it
    // did: after the locator crashed on page 14 of JJ 4 TEMPS it retried with
    // anchor:{x:595,y:330}, landing a leader line on empty floor in the wrong
    // render. Guessing coordinates over a whole page is the single failure this
    // module exists to remove, so a 5xx now asks for a retry, never a guess.
    if (res.status >= 500) {
      throw new Error(
        `SLIDES_ADD_ELEMENT: the anchor service failed on page ${page.index + 1} (${res.status}) while locating "${element}", after 3 attempts. ${detail.slice(0, 160)} This is a server fault, NOT a problem with your description: keep the same anchorElement and try the command again. Do not substitute a numeric anchor.`
      );
    }
    // Out of credits is not a bad description either, and re-wording only burns
    // more turns: say so plainly and stop the review rather than sending the
    // agent round the re-phrasing loop for every remaining callout.
    if (res.status === 402) {
      throw new Error(
        `SLIDES_ADD_ELEMENT: out of credits, so "${element}" on page ${page.index + 1} could not be located. This is a billing limit, NOT a problem with your description: stop placing callouts and tell the user their account is out of credits.`
      );
    }
    throw new Error(
      `SLIDES_ADD_ELEMENT: could not locate "${element}" on page ${page.index + 1} (${res.status}). ${detail.slice(0, 160)} Re-word anchorElement to name a visible object, or use anchorText if a label sits on it.`
    );
  }
  return (await res.json()) as LocatedAnchorResponse;
};

const VALID_ELEMENT_TYPES = new Set(['note', 'arrow', 'rect', 'ellipse', 'text', 'stamp', 'image', 'patch']);
const VALID_VERDICTS = new Set<SlideReviewVerdict>(['pass', 'warn', 'fail', 'missing', 'na']);
const REVIEW_POINT_IDS = new Set(['p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7', 'p8', 'p9', 'p10']);

/** Named page sizes in PDF points. Without these the agent had to guess raw
 *  numbers, and nothing in its prompt told it what A4 actually is - so a
 *  synthesis page appended to an A4 deck silently came out 16:9. */
const PAGE_SIZE_PRESETS: Record<string, { width: number; height: number }> = {
  '16:9': { width: 960, height: 540 },
  '4:3': { width: 960, height: 720 },
  A4_landscape: { width: 842, height: 595 },
  A4_portrait: { width: 595, height: 842 },
  letter_landscape: { width: 792, height: 612 },
};

/**
 * The counter-proposal is an INSTRUCTION-driven maskless edit of a real
 * photograph: keep this unit, change the things the findings named. It runs
 * through the shared Studio pipeline, so eligibility, the access scope, the
 * credit reservation, the refund, the GenerationLog row and the workspace
 * version all come from the path that already owns them.
 *
 * The flagship, deliberately. This was briefly switched to nano-banana-pro on the
 * strength of its doc block ("large-scale instruction-driven image edits") - which
 * was reading half the registry. The same entry says defaultVisible: false,
 * lifecycle: 'legacy', "Legacy model kept for backward compatibility", and
 * cheapEligible: FALSE, so a team on CHEAP_MODELS_ONLY would have been refused at
 * eligibility and got no proposal at all. It also cost twice as much (2 credits a
 * generation against 1) and measured no better: 0.38 on pro where the flagship
 * scored 0.42, on the same page.
 *
 * What actually fixed the quality was neither model: it was turning search
 * grounding off, and correcting a brief that had been demanding the tenant empty
 * their own windows.
 */
const COUNTER_PROPOSAL_MODEL = FLAGSHIP_STUDIO_MODEL_ID;

/**
 * Ask the critic whether a draft can be sent.
 *
 * Returns null when the critic could not be reached, and the caller treats that
 * as UNJUDGED - never as approval. The whole point of the loop is that no image
 * reaches a tenant on nobody's say-so, and a critic that 500s is not a critic
 * that said yes.
 */
const critiqueProposal = async (
  beforeUrl: string,
  afterUrl: string,
  changes: string[],
  projectId: string,
  // Which question to ask. The croquis pass is judged on whether the handwriting
  // is legible and matches the remarks; the design pass is judged on whether the
  // corrections were applied and NO lettering was drawn in. Asking the wrong one
  // fails a good draft on the rule the other pass exists to enforce.
  mode: 'design' | 'croquis',
  // Stopping a review must stop the critic too: without this the request runs to
  // completion after the user pressed stop, and bills for a verdict nobody wants
  // (AGENTS.md 2.1, deep abort propagation).
  abortSignal?: AbortSignal
): Promise<ProposalVerdict | null> => {
  let res: Response;
  try {
    // fetchApi, NOT fetch. A review runs for minutes and will eventually overlap a
    // Clerk token refresh, which answers with the sign-in page wearing an HTTP
    // 200: res.ok is true, res.json() throws, and this function would return null
    // - "unjudged" - which the loop treats as placeable. The one failure mode that
    // puts an unexamined image in front of a tenant is the one this call must not
    // have, and the branch already carries the retry built for exactly it.
    res = await fetchApi('/api/slides/proposal-critic', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ beforeUrl, afterUrl, changes, projectId, mode }),
      signal: abortSignal,
    });
  } catch {
    abortSignal?.throwIfAborted();
    return null;
  }
  if (!res.ok) {
    if (res.status >= 400 && res.status < 500 && res.status !== 429) {
      const payload = await res.json().catch(() => null);
      throw new Error(`Proposal could not be checked: ${payload?.message || `request refused (${res.status})`}`);
    }
    return null;
  }
  return await res.json().catch(() => null) as ProposalVerdict | null;
};


const requireDeck = (commandName: string, projectId?: string): SlideDeckDTO => {
  const deck = resolveAgentSlideDeck(projectId);
  if (!deck) throw new Error(`${commandName}: no deck is open in the Slides tool.`);
  // Heartbeat for the "Working on it…" chip: every executed command refreshes it.
  useSlidesStore.getState().markAgentActive();
  return deck;
};

/**
 * Everything a new/edited callout bubble must not cover on this page: the other
 * bubbles, plus every opaque block the exporter paints over the annotations -
 * the validation card on page 1. A bubble under it is
 * simply gone from the exported deliverable: the reviewer reads the finding in
 * the chat transcript and finds nothing on the drawing.
 */
const noteBlockers = (deck: SlideDeckDTO, page: SlidePageDoc, excludeId?: string): Frame[] => {
  const frames = page.elements
    .filter((e): e is Extract<SlideElement, { type: 'note' }> => e.type === 'note' && e.id !== excludeId)
    .map((e) => e.frame);
  frames.push(...reservedExportRegions(deck.doc.review?.stamp, deck.doc.pages.findIndex((p) => p.id === page.id), page.width, page.height));
  return frames;
};

/** Accepts a 1-based page number or a page id. */
const resolvePage = (deck: SlideDeckDTO, ref: unknown, commandName: string): SlidePageDoc => {
  if (typeof ref === 'number' && Number.isFinite(ref)) {
    const page = deck.doc.pages[Math.round(ref) - 1];
    if (page) return page;
    throw new Error(`${commandName}: page ${ref} is out of range (deck has ${deck.doc.pages.length} pages).`);
  }
  if (typeof ref === 'string' && ref.trim()) {
    const page = deck.doc.pages.find((p) => p.id === ref.trim());
    if (page) return page;
    const asNum = Number(ref.trim());
    const byNumber = Number.isFinite(asNum) ? deck.doc.pages[asNum - 1] : undefined;
    if (byNumber) return byNumber;
    throw new Error(`${commandName}: unknown page "${ref}".`);
  }
  throw new Error(`${commandName}: "page" is required (1-based page number or page id).`);
};

const parseFrame = (raw: unknown, commandName: string): SlideElement['frame'] => {
  const f = (raw ?? {}) as Record<string, unknown>;
  const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : NaN);
  const frame = { x: num(f.x), y: num(f.y), w: num(f.w), h: num(f.h) };
  if ([frame.x, frame.y, frame.w, frame.h].some((v) => Number.isNaN(v))) {
    throw new Error(`${commandName}: "frame" must be { x, y, w, h } in page points.`);
  }
  return frame;
};

/** Validated patch builder for SLIDES_UPDATE_ELEMENT.
 *
 *  The previous implementation spread the raw patch and coerced anything it
 *  disliked to `undefined`, which meant a bad value silently ERASED the field
 *  (`fontSize: "18"` wiped the size) and unknown keys were written verbatim
 *  onto the element. Both are worse than failing: the agent got no signal and
 *  the document quietly rotted. Now every key is whitelisted and every value
 *  validated, with a message the agent can act on. */
const buildElementPatch = (raw: Record<string, unknown>, commandName: string): Partial<SlideElement> => {
  const out: Record<string, unknown> = {};
  const reject = (field: string, expected: string): never => {
    throw new Error(`${commandName}: "${field}" must be ${expected} (got ${JSON.stringify(raw[field])}).`);
  };
  const has = (k: string) => Object.prototype.hasOwnProperty.call(raw, k);
  const posNum = (k: string) => {
    const v = raw[k];
    if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) reject(k, 'a positive number');
    out[k] = v;
  };
  const oneOf = (k: string, allowed: readonly string[]) => {
    const v = raw[k];
    if (typeof v !== 'string' || !allowed.includes(v)) reject(k, `one of ${allowed.join('|')}`);
    out[k] = v;
  };

  if (has('frame')) out.frame = parseFrame(raw.frame, commandName);
  if (has('text')) {
    if (typeof raw.text !== 'string') reject('text', 'a string');
    out.text = raw.text;
  }
  if (has('color')) {
    const v = raw.color;
    if (typeof v !== 'string' || !/^#?[0-9a-fA-F]{3,8}$/.test(v.trim())) reject('color', 'a hex colour like "#FF3B30"');
    out.color = (v as string).trim();
  }
  if (has('background')) {
    const v = raw.background;
    if (typeof v !== 'string' || !/^#?[0-9a-fA-F]{3,8}$/.test(v.trim())) reject('background', 'a hex colour');
    out.background = (v as string).trim();
  }
  if (has('opacity')) {
    const v = raw.opacity;
    if (typeof v !== 'number' || !Number.isFinite(v) || v < 0 || v > 1) reject('opacity', 'a number from 0 to 1');
    out.opacity = v;
  }
  if (has('rotation')) {
    const v = raw.rotation;
    if (typeof v !== 'number' || !Number.isFinite(v)) reject('rotation', 'a number of degrees');
    out.rotation = v;
  }
  if (has('guidelinePointId')) {
    const v = raw.guidelinePointId;
    if (v === null || v === '') out.guidelinePointId = undefined;
    else if (typeof v === 'string' && REVIEW_POINT_IDS.has(v)) out.guidelinePointId = v;
    else reject('guidelinePointId', 'one of p1..p10 (or null to clear)');
  }
  if (has('severity')) oneOf('severity', ['info', 'warn', 'fail']);
  if (has('verdict')) oneOf('verdict', ['pass', 'warn', 'fail', 'missing', 'na']);
  if (has('align')) oneOf('align', ['left', 'center', 'right']);
  if (has('fontWeight')) oneOf('fontWeight', ['normal', 'bold']);
  if (has('fontFamily')) oneOf('fontFamily', ['sans', 'serif', 'mono']);
  if (has('fontSize')) posNum('fontSize');
  if (has('lineHeight')) posNum('lineHeight');
  if (has('strokeWidth')) posNum('strokeWidth');
  if (has('italic')) {
    if (typeof raw.italic !== 'boolean') reject('italic', 'true or false');
    out.italic = raw.italic;
  }
  if (has('fill')) {
    if (typeof raw.fill !== 'boolean') reject('fill', 'true or false');
    out.fill = raw.fill;
  }
  if (has('url')) {
    if (typeof raw.url !== 'string' || !raw.url.trim()) reject('url', 'a non-empty string');
    out.url = (raw.url as string).trim();
  }
  if (has('anchorText')) {
    const v = raw.anchorText;
    if (typeof v !== 'string' || v.trim().length < 2) reject('anchorText', 'a label string of ≥2 characters (e.g. "ZONE CAISSES")');
    // Transient: resolved to exact coordinates against the page's text map by the
    // ADD/UPDATE handlers, then dropped - never persisted on the element.
    (out as { anchorText?: string }).anchorText = (v as string).trim();
  }
  if (has('anchorElement')) {
    const v = raw.anchorElement;
    if (typeof v !== 'string' || v.trim().length < 3) {
      reject('anchorElement', 'a short description of the target (e.g. "the hanging COMMANDEZ ICI sign in VUE 1")');
    }
    // Transient: resolved to exact coordinates by the ADD/UPDATE handlers via
    // /api/slides/locate-anchor, then dropped - never persisted on the element.
    (out as { anchorElement?: string }).anchorElement = (v as string).trim();
  }
  if (has('anchor')) {
    const a = raw.anchor as { x?: unknown; y?: unknown } | null;
    if (!a || typeof a.x !== 'number' || typeof a.y !== 'number' || !Number.isFinite(a.x) || !Number.isFinite(a.y)) {
      reject('anchor', 'an { x, y } point in page points');
    } else {
      (out as { anchor?: { x: number; y: number } }).anchor = { x: a.x, y: a.y };
    }
  }

  const known = new Set([
    'frame', 'text', 'color', 'background', 'opacity', 'rotation', 'guidelinePointId', 'severity',
    'verdict', 'align', 'fontWeight', 'fontFamily', 'fontSize', 'lineHeight', 'strokeWidth', 'italic',
    'fill', 'url', 'anchor', 'anchorText', 'anchorElement', 'id', 'type',
  ]);
  const unknown = Object.keys(raw).filter((k) => !known.has(k));
  if (unknown.length > 0) {
    throw new Error(
      `${commandName}: unknown field(s) ${unknown.map((k) => `"${k}"`).join(', ')}. Editable fields: frame, text, color, background, opacity, rotation, guidelinePointId, severity, verdict, align, fontWeight, fontFamily, fontSize, lineHeight, strokeWidth, italic, fill, url, anchor.`
    );
  }
  return out as Partial<SlideElement>;
};

const sanitizeElement = (raw: unknown, commandName: string): SlideElement => {
  if (!raw || typeof raw !== 'object') throw new Error(`${commandName}: "element" must be an object.`);
  const el = raw as Record<string, unknown>;
  const type = typeof el.type === 'string' ? el.type : '';
  if (!VALID_ELEMENT_TYPES.has(type)) {
    throw new Error(`${commandName}: element.type must be one of note|arrow|rect|ellipse|text|stamp|image|patch.`);
  }
  const base = {
    id: typeof el.id === 'string' && el.id.trim() ? el.id.trim() : slidesGenId(),
    type,
    frame: parseFrame(el.frame, commandName),
    color: typeof el.color === 'string' ? el.color : undefined,
    opacity: typeof el.opacity === 'number' ? el.opacity : undefined,
    rotation: typeof el.rotation === 'number' ? el.rotation : undefined,
    guidelinePointId:
      typeof el.guidelinePointId === 'string' && REVIEW_POINT_IDS.has(el.guidelinePointId)
        ? el.guidelinePointId
        : undefined,
  };
  const text = typeof el.text === 'string' ? el.text : undefined;
  switch (type) {
    case 'note': {
      if (!text?.trim()) throw new Error(`${commandName}: a note element requires non-empty "text".`);
      const a = el.anchor as { x?: unknown; y?: unknown } | undefined;
      const anchor =
        a && typeof a.x === 'number' && typeof a.y === 'number' && Number.isFinite(a.x) && Number.isFinite(a.y)
          ? { x: a.x, y: a.y }
          : undefined;
      // The two TRANSIENT targeting fields must survive sanitising: this builds a
      // fresh object from a fixed field list, so anything not named here is
      // dropped before the handler ever sees it. That silently killed BOTH
      // anchorText (its "deterministic, never misses" branch in
      // SLIDES_ADD_ELEMENT was unreachable) and anchorElement - the note kept
      // only the model's rough numeric anchor, which is exactly the coordinate
      // guess both fields exist to replace. The handler deletes them after
      // resolving, so they are still never persisted.
      const anchorText =
        typeof el.anchorText === 'string' && el.anchorText.trim().length >= 2 ? el.anchorText.trim() : undefined;
      const anchorElement =
        typeof el.anchorElement === 'string' && el.anchorElement.trim().length >= 3
          ? el.anchorElement.trim()
          : undefined;
      return {
        ...base,
        type: 'note',
        text,
        // RDM review comments default to blue (info); the stamp carries the verdict.
        severity: el.severity === 'warn' || el.severity === 'fail' || el.severity === 'info' ? el.severity : 'info',
        ...(anchor ? { anchor } : {}),
        ...(anchorText ? { anchorText } : {}),
        ...(anchorElement ? { anchorElement } : {}),
      } as SlideElement;
    }
    case 'text':
      if (!text?.trim()) throw new Error(`${commandName}: a text element requires non-empty "text".`);
      return {
        ...base,
        type: 'text',
        text,
        fontSize: typeof el.fontSize === 'number' && el.fontSize > 0 ? el.fontSize : undefined,
        fontWeight: el.fontWeight === 'bold' ? 'bold' : el.fontWeight === 'normal' ? 'normal' : undefined,
        italic: el.italic === true ? true : undefined,
        align: el.align === 'center' || el.align === 'right' || el.align === 'left' ? el.align : undefined,
        lineHeight: typeof el.lineHeight === 'number' && el.lineHeight > 0 ? el.lineHeight : undefined,
        fontFamily:
          el.fontFamily === 'serif' || el.fontFamily === 'mono' || el.fontFamily === 'sans'
            ? el.fontFamily
            : undefined,
      };
    case 'stamp':
      if (!text?.trim()) throw new Error(`${commandName}: a stamp element requires non-empty "text".`);
      return {
        ...base,
        type: 'stamp',
        text,
        verdict: VALID_VERDICTS.has(el.verdict as SlideReviewVerdict) ? (el.verdict as SlideReviewVerdict) : undefined,
      };
    case 'image':
      if (typeof el.url !== 'string' || !el.url.trim()) {
        throw new Error(`${commandName}: an image element requires "url".`);
      }
      return { ...base, type: 'image', url: el.url };
    case 'patch':
      return {
        ...base,
        type: 'patch',
        text,
        background: typeof el.background === 'string' ? el.background : '#FFFFFF',
        fontSize: typeof el.fontSize === 'number' ? el.fontSize : undefined,
      };
    case 'arrow':
      return { ...base, type: 'arrow', text, strokeWidth: typeof el.strokeWidth === 'number' ? el.strokeWidth : undefined };
    case 'rect':
      return {
        ...base,
        type: 'rect',
        text,
        strokeWidth: typeof el.strokeWidth === 'number' ? el.strokeWidth : undefined,
        fill: el.fill === true ? true : undefined,
      };
    case 'ellipse':
      return {
        ...base,
        type: 'ellipse',
        text: typeof el.text === 'string' ? el.text : undefined,
        strokeWidth: typeof el.strokeWidth === 'number' ? el.strokeWidth : undefined,
        fill: el.fill === true ? true : undefined,
      };
    default:
      throw new Error(`${commandName}: unsupported element type "${type}".`);
  }
};

export const SLIDES_COMMAND_DEFINITIONS: Record<string, UiCommandDefinition> = {
  ...SLIDES_RESEARCH_COMMANDS,
  SLIDES_READ_PAGE: {
    action_type: 'SLIDES_READ_PAGE',
    traceLabels: { active: 'viewing', done: 'viewed', unit: 'page' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Views a page of the open deck: focuses it in the editor and attaches its rendered IMAGE to your next pass (your script auto-continues). This is your eyes - always view a page before making a visual judgement about it. The page\'s extracted text is already in your live deck context.',
    parameters: [
      { name: 'page', type: 'number', description: '1-based page number (or a page id string).', required: true },
    ],
    availableInModes: ['slides'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const deck = requireDeck('SLIDES_READ_PAGE', options?.projectId);
      const page = resolvePage(deck, params.page, 'SLIDES_READ_PAGE');
      const st = useSlidesStore.getState();
      st.focusPage(deck.projectId, page.id);
      st.requestVision(page.id);
      // Mark it as being READ, which the canvas shows as a slow pulse: the agent
      // spends real seconds looking at a sheet before it says anything, and
      // without a signal that reads as the tool having stalled.
      st.setReadingPage(page.id);
      st.markAgentActive();
      // A page is viewable when it has an imported PDF snapshot OR carries its
      // own image elements (authored 'blank' pages never get a snapshotUrl -
      // slidesAgentContext attaches their image elements instead). Only a page
      // with neither is genuinely un-viewable.
      const hasImageElement = page.elements.some((el) => el.type === 'image' && !!el.url);
      if (!page.snapshotUrl && !hasImageElement) {
        throw new Error(
          `SLIDES_READ_PAGE: page ${page.index + 1} has nothing to view - it has no imported snapshot and no image element. Its text and annotations are already in your deck context.`
        );
      }
    },
  },

  SLIDES_GOTO_PAGE: {
    action_type: 'SLIDES_GOTO_PAGE',
    traceLabels: { active: 'navigating', done: 'navigated', unit: 'page' },
    targetEntityType: 'ABSOLUTE',
    description: 'Navigates the editor to a page (focuses it for the user) without requesting its image.',
    parameters: [
      { name: 'page', type: 'number', description: '1-based page number (or a page id string).', required: true },
    ],
    availableInModes: ['slides'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const deck = requireDeck('SLIDES_GOTO_PAGE', options?.projectId);
      const page = resolvePage(deck, params.page, 'SLIDES_GOTO_PAGE');
      useSlidesStore.getState().focusPage(deck.projectId, page.id);
    },
  },

  SLIDES_ADD_ELEMENT: {
    action_type: 'SLIDES_ADD_ELEMENT',
    traceLabels: { active: 'annotating', done: 'annotated', unit: 'element', pluralUnit: 'elements' },
    targetEntityType: 'ABSOLUTE',
    description: `Places one or more overlay elements (annotations) on a page of the open deck. Element types: "note" (localized review comment - your workhorse: a blue callout box (frame = the bubble, placed in a MARGIN) with a leader line to "anchor":{x,y}, the exact spot ON the drawing the comment concerns. colour-code by STATUS with the three-value convention ONLY: severity 'fail' = RED (to correct), severity 'info' = BLUE (neutral/request), color '#2E8B57' = GREEN (validated) - orange does not exist in the deliverable. ALWAYS set an anchor - a note without an anchor is just a floating box; each finding gets ITS OWN anchor on ITS OWN element (shared dots are rejected), and the dot must land ON drawn content (empty-paper anchors are rejected)), "arrow" (frame encodes the vector: from (x,y) to (x+w,y+h); point AT the issue), "rect"/"ellipse" (frame a zone), "text" (real authored slide text - see below), "stamp" (verdict chip like "P3 - FAIL"), "patch" (covers base content with a background color + optional replacement text), "image" (places a picture - requires "url"; pass the url of an image the user attached to the conversation to drop it onto a slide, and size the frame to the page for a full-bleed image). Coordinates are PAGE POINTS from the page's top-left; page sizes are in your deck context. Review findings must carry guidelinePointId ('p1'..'p10'). Note colours: RED #FF3B30 (fail), BLUE #2B5CAD (info), GREEN #2E8B57 (validated) - the three-value code, nothing else.\n\nText elements render as genuine slide text (identical on the editor canvas and the exported PDF): the text WRAPS inside its frame box (width = frame.w) and you control fontSize (pt, default 16), fontWeight ('normal'|'bold'), italic (bool), align ('left'|'center'|'right'), lineHeight (multiplier, default 1.25), fontFamily ('sans'|'serif'|'mono'), and color (hex). Size the frame.h to fit the wrapped lines (overflow is clipped). Fonts are the PDF standard set only (Helvetica/Times/Courier).`,
    parameters: [
      { name: 'page', type: 'number', description: '1-based page number (or a page id string).', required: true },
      {
        name: 'elements',
        type: 'object[]',
        description:
          'Elements to add. Each: { type, frame:{x,y,w,h}, text?, severity?, anchorElement?, anchorText?, anchor?:{x,y}, color?, guidelinePointId?, verdict?, background?, url?, strokeWidth?, fontSize? }. A "note" MUST be anchored. THREE ways, in order of preference: (1) **anchorElement** - DESCRIBE the object the comment is about in your own words ("the hanging COMMANDEZ ICI sign in VUE 1", "the opaque central wall panel in VUE 3"); the app finds it visually and computes the exact point. This is the right choice on renders, elevations and photographs, i.e. most pages. (2) **anchorText** - name a text label on/near the target ("ZONE CAISSES"); the app resolves it from the PDF text layer, exact, when the page HAS a text layer (see TEXT ANCHORS in your context - if that block is absent the page has none, so use anchorElement). A raw anchor:{x,y} ON ITS OWN IS REFUSED on any page that has a rendered snapshot, which is every page of a normal deck: estimating coordinates over a whole sheet is where mispointed leader lines come from, so the app does not accept the estimate. You may still pass a rough anchor ALONGSIDE anchorElement as a hint; the app owns the final point either way. If the anchor service returns an error, RETRY the same anchorElement - do not switch to coordinates. Note bubbles are auto-clamped inside the page and auto-shifted to not overlap other bubbles, nor the validation stamp that covers the top-left of page 1 - place them in a free margin (on page 1, below the stamp or to its right); the app guarantees bounds, and re-sending a position it already moved will just move it again. Note frame w is a WIDTH HINT (clamped 140–300) and frame h is IGNORED: the app computes the exact box height from the wrapped text, so the bubble always fits its text exactly. Text elements also accept: fontWeight ("normal"|"bold"), italic (bool), align ("left"|"center"|"right"), lineHeight (multiplier), fontFamily ("sans"|"serif"|"mono").',
        required: true,
        example: [
          {
            type: 'note',
            frame: { x: 40, y: 240, w: 230, h: 60 },
            anchorElement: 'the cash desk unit in the centre of the plan',
            text: 'La zone caisses ne peut pas être implantée dans les 3 premiers mètres (p4).',
            severity: 'fail',
            guidelinePointId: 'p4',
          },
        ],
      },
    ],
    availableInModes: ['slides'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const deck = requireDeck('SLIDES_ADD_ELEMENT', options?.projectId);
      const page = resolvePage(deck, params.page, 'SLIDES_ADD_ELEMENT');
      const rawElements = Array.isArray(params.elements)
        ? params.elements
        : params.element
          ? [params.element]
          : [];
      if (rawElements.length === 0) {
        throw new Error('SLIDES_ADD_ELEMENT: provide at least one element in "elements".');
      }
      const st = useSlidesStore.getState();
      const reviewRun = options?.sessionId ? st.getReviewRun(options.sessionId) : null;
      const parsed = rawElements.map((raw: unknown) => sanitizeElement(raw, 'SLIDES_ADD_ELEMENT'));

      // DETERMINISTIC GEOMETRY (the app owns placement, not the model):
      // 1. anchorText → exact label coordinates from the page's PDF text map;
      // 2. anchors clamped into the page;
      // 3. note bubbles clamped in-bounds and slid to a non-overlapping slot
      //    (vs existing notes, the rest of this batch AND the validation stamp)
      //    - nothing renders off-canvas, nothing covers another callout, and
      //    nothing disappears under the opaque stamp card.
      const placedFrames = noteBlockers(deck, page);
      // How many callouts this sheet should carry in total. The budget is also
      // in the agent's context, but advice it can ignore is not a limit: told
      // only to be concise it still filled a 66%-drawn section sheet with six
      // comments, which the reviewer rejected as unreadable. So it binds here.
      const existingNotes = page.elements.filter((e) => e.type === 'note').length;
      // The budget is the page's TOTAL allowance, so it is measured against an
      // empty sheet - only the opaque export regions block. Measuring it against
      // the page as it stands returns "how many MORE fit", and comparing a total
      // to a remainder let every new command grant a fresh allowance: a page
      // capped at 3 reached 7 across several commands, which is the "too much"
      // the reviewer rejected, creeping back in.
      const budget = await countFreeNoteSlots(
        page,
        TYPICAL_NOTE,
        reservedExportRegions(deck.doc.review?.stamp, deck.doc.pages.findIndex((p) => p.id === page.id), page.width, page.height),
        page.width,
        page.height
      );
      // Anchors of the page's existing agent notes + this batch: two findings
      // must never share a dot (the user-reported "two lines, one spot" defect).
      const takenAnchors: Array<{ x: number; y: number }> = page.elements
        .filter((e): e is Extract<SlideElement, { type: 'note' }> => e.type === 'note' && !!e.anchor)
        .map((e) => e.anchor!);
      // BUDGET FIRST, before a single vision call. This check used to sit inside
      // the loop, after locateAnchorForElement had already spent up to five opus
      // calls per note: a batch that overflowed the budget burned the credits and
      // then threw, and because the store writes happen after the loop, the notes
      // that HAD resolved were discarded too. Refusing up front costs nothing and
      // tells the agent exactly how many it may send.
      const requestedNotes = parsed.filter((el) => el.type === 'note').length;
      if (budget !== null && requestedNotes > 0 && existingNotes + requestedNotes > budget) {
        const room = Math.max(0, budget - existingNotes);
        throw new Error(
          `SLIDES_ADD_ELEMENT: page ${page.index + 1} takes at most ${budget} callout(s) and already has ${existingNotes}, so there is room for ${room} more, but you sent ${requestedNotes}. It is a dense sheet and more comments on it stop being readable. Send ${room === 0 ? 'none: put these findings in the synthesis instead' : `at most ${room}`}, or merge related comments into one with SLIDES_UPDATE_ELEMENT. Do not retry the same set.`
        );
      }

      const guarded: SlideElement[] = [];
      // Callouts this batch could not place, and why. They are REPORTED at the
      // end rather than thrown on the spot: throwing mid-loop discarded every
      // callout already resolved, which on a real review meant a whole run
      // placed nothing. Measured on the FEVER-ROSNY deck: 82 add attempts, 24
      // refusals across pages 4, 6, 7 and 9, fifteen agent turns, and ZERO
      // annotations on the deck at the end, because every batch contained one
      // target the locator could not find and that one took the batch with it.
      // Each refusal also re-ran the locator on the survivors, so the run paid
      // for the same callouts several times over and still showed nothing.
      const skipped: string[] = [];
      for (const el of parsed) {
        const anchorText = (el as { anchorText?: string }).anchorText;
        if (anchorText !== undefined) delete (el as { anchorText?: string }).anchorText;
        const anchorElement = (el as { anchorElement?: string }).anchorElement;
        if (anchorElement !== undefined) delete (el as { anchorElement?: string }).anchorElement;
        if (el.type !== 'note') {
          guarded.push(el);
          continue;
        }
        // THREE-VALUE COLOUR CODE (green/red/blue - the team's convention):
        // orange is not part of the deliverable; a 'warn' note renders blue.
        if ((el as { severity?: string }).severity === 'warn') {
          (el as { severity?: 'info' | 'warn' | 'fail' }).severity = 'info';
        }
        let anchor = (el as { anchor?: { x: number; y: number } }).anchor;
        // Grounded anchors come back already on the element, verified inside an
        // enlarged crop of the panel - the ink probe below must not second-guess
        // them (on a photographic render it accepts 88% of the page anyway).
        let grounded = false;
        if (anchorElement) {
          const located = await locateAnchorForElement(
            page,
            anchorElement,
            (el as { text?: string }).text,
            anchor,
            deck.projectId
          );
          // Only a LOST anchor is refused. The locator was first wired to refuse
          // anything it had not positively verified, and that lost notes the
          // user had asked for: it rejected correct points over the wording of
          // the description, the agent spent its turn re-phrasing, and one of
          // four requested callouts never landed at all. A best-effort dot on
          // the right region is worth more than a missing note; a dot the model
          // says is on some other object entirely is not, and still errors.
          if (located.confidence === 'lost') {
            skipped.push(
              `"${anchorElement}": the locator checked ${located.attempts} candidate point(s) and ruled that none is on it; the best was on "${located.whatIsThere}"`
            );
            continue;
          }
          anchor = { x: located.x, y: located.y };
          stampLocateCredits(el as { credits?: number }, located);
          // ONLY a verified point may skip the ink probe below. An unverified
          // one is still the best available and is worth drawing, but nothing
          // confirmed it, so it earns no more trust than a hand-written
          // coordinate and goes through the same empty-paper check.
          grounded = located.confidence === 'verified';
          console.info(
            `[Slides] anchorElement "${anchorElement}" → (${Math.round(located.x)},${Math.round(located.y)}) in region P${located.region} on page ${page.index + 1}, ${located.confidence} after ${located.attempts} candidate(s): ${located.whatIsThere}`
          );
        } else if (anchorText) {
          // anchorText was the LAST unverified path, and it is how a leader line
          // still landed on random text. It is a fuzzy match over the PDF text
          // layer - exact, then contains, then contained - so a comment about
          // "Pink wall tiles" resolved to whichever label happened to contain
          // that phrase, which on a real sheet was a line in the specification
          // panel rather than the pink wall in the drawing. Deterministic is not
          // the same as correct.
          //
          // The text-map hit is now a HINT into the same verified visual locator
          // that anchorElement uses: the label still steers the search, but the
          // point has to survive the on-element check before it is drawn.
          const hit = resolveAnchorText(page.textMap, anchorText);
          if (!hit && !anchor) {
            skipped.push(
              `"${anchorText}": matches no label on page ${page.index + 1}. Use a label visible on the page (see TEXT ANCHORS in your context), or name the object with anchorElement`
            );
            continue;
          }
          if (page.snapshotUrl) {
            const located = await locateAnchorForElement(
              page,
              anchorText,
              (el as { text?: string }).text,
              hit ?? anchor,
              deck.projectId
            );
            if (located.confidence === 'lost') {
              skipped.push(
                `"${anchorText}": that label could not be tied to the thing the comment is about; the best point was on "${located.whatIsThere}". Describe the OBJECT with anchorElement instead of naming a label`
              );
              continue;
            }
            anchor = { x: located.x, y: located.y };
            stampLocateCredits(el as { credits?: number }, located);
            grounded = located.confidence === 'verified';
            console.info(
              `[Slides] anchorText "${anchorText}" → (${Math.round(located.x)},${Math.round(located.y)}) on page ${page.index + 1}, ${located.confidence}: ${located.whatIsThere}`
            );
          } else if (hit) {
            anchor = hit;
          }
        } else if (page.snapshotUrl) {
          // NO GUESSED COORDINATES. A bare numeric anchor on a rendered page is
          // the model estimating {x,y} over a whole A3 sheet, which is the
          // measured failure this module was built to remove: 2 of 5 leader
          // lines off their element, one in the white margin. Telling the agent
          // it is a "last resort" was not enough - the moment the locator
          // errored it guessed (595,330) and put a line on empty floor in the
          // wrong render. So the guess is refused, not discouraged: on a page
          // that HAS a snapshot the target must be named, and naming is a
          // capability the app owns and can retry, not one the model estimates.
          throw new Error(
            `SLIDES_ADD_ELEMENT: the note "${String((el as { text?: string }).text ?? '').slice(0, 40)}…" on page ${page.index + 1} carries a raw anchor {x,y} and no target name. Estimating coordinates over a whole page is not reliable enough to point a leader line, so it is not accepted here. Set anchorElement to describe the object the comment is about (preferred), or anchorText to a label sitting on it. You may keep the {x,y} alongside anchorElement as a hint.`
          );
        }
        // Every note MUST point at something - a note with no anchor renders as a
        // floating bubble with NO leader line (the reported "pointing nowhere"
        // defect). Require a target; the reliable one is anchorText.
        if (!anchor) {
          throw new Error(
            `SLIDES_ADD_ELEMENT: the note "${String((el as { text?: string }).text ?? '').slice(0, 40)}…" has no anchor, so it would point at nothing. Give it a target: set anchorElement to name the object it flags (preferred on graphic pages), or anchorText to a label from TEXT ANCHORS.`
          );
        }
        anchor = clampAnchorToPage(anchor, page.width, page.height);
        if (!grounded) {
          // Fix a near-miss: snap the dot to the nearest drawn pixel (an anchor on
          // empty paper is corrected, not rejected). Only truly-lost anchors fail.
          const snapped = await snapAnchorToInk(page, anchor);
          if (snapped === null) {
            throw new Error(
              `SLIDES_ADD_ELEMENT: anchor (${Math.round(anchor.x)},${Math.round(anchor.y)}) on page ${page.index + 1} is far from any drawn element (empty paper). Aim at the element: set anchorElement to name it, or use anchorText with a label from TEXT ANCHORS.`
            );
          }
          anchor = snapped;
        }
        // Distinct findings, distinct spots: nudge off another callout's dot to
        // the nearest free ink rather than stacking two leader lines on one point.
        //
        // The nudge is bounded twice over. Each probe is clamped into the page,
        // so a dot near the right or bottom edge does not spiral out from an
        // off-page centre. And the dot may not drift further than
        // ANCHOR_DRIFT_MAX_PT from where the finding aimed: walking +16/+12 eight
        // times moves it ~160pt, which on an A3 page is a different part of the
        // drawing entirely - a leader line confidently pointing at the wrong
        // element is worse than two lines sharing one.
        const aimedAt = anchor;
        const clashesWith = (a: { x: number; y: number }) =>
          takenAnchors.find((t) => Math.hypot(t.x - a.x, t.y - a.y) < ANCHOR_CLASH_PT);
        let clash = clashesWith(anchor);
        for (let tries = 0; clash && tries < 8; tries++) {
          const probe = clampAnchorToPage({ x: anchor.x + 16, y: anchor.y + 12 }, page.width, page.height);
          const nudged = await snapAnchorToInk(page, probe);
          if (!nudged || Math.hypot(nudged.x - anchor.x, nudged.y - anchor.y) < 1) break;
          const next = clampAnchorToPage(nudged, page.width, page.height);
          if (Math.hypot(next.x - aimedAt.x, next.y - aimedAt.y) > ANCHOR_DRIFT_MAX_PT) break;
          anchor = next;
          clash = clashesWith(anchor);
        }
        if (clash) {
          // Not fatal - a shared dot still shows the finding - but it must not be
          // silent: the knowledge base tells the agent this is enforced, and the
          // reviewer sees two leader lines converging on one spot with nothing
          // explaining why.
          console.warn(
            `[Slides] Two findings share an anchor on page ${page.index + 1} near (${Math.round(anchor.x)},${Math.round(anchor.y)}): no free ink within ${ANCHOR_DRIFT_MAX_PT}pt to separate them.`
          );
        }
        takenAnchors.push(anchor);
        (el as { anchor?: { x: number; y: number } }).anchor = anchor;
        // Exact-fit box: the app computes the bubble size from the text (the
        // model's w is a hint, its h is ignored) - text can never overflow and
        // boxes are never oversized.
        const size = await noteFrameForText(
          (el as { text?: string }).text ?? '',
          el.frame.w,
          page.width,
          page.height
        );
        const sized = { x: el.frame.x, y: el.frame.y, w: size.w, h: size.h };
        // READABILITY IS PART OF CORRECTNESS. A bubble that is individually
        // right but sits on the drawing, or on another bubble, makes the review
        // unusable - the reviewer cannot see the thing being reviewed. A human
        // parks every bubble in the page margin and lets the leader line point;
        // this searches blank paper for the same result, and only falls back to
        // the pure-geometry resolver when there is no snapshot to measure.
        const clear = await placeNoteInClearSpace(
          page,
          size,
          anchor,
          placedFrames,
          page.width,
          page.height
        );
        // Two bars, not one. Anything up to MAX_TOLERABLE_COVERAGE is placed -
        // on a dense sheet the least-covered slot is the honest best answer, and
        // a review that says nothing is no better than one that is illegible.
        // Only when even the best slot would bury the drawing is the page full.
        if (clear && clear.coverage > MAX_TOLERABLE_COVERAGE && page.snapshotUrl) {
          // SKIP THIS ONE BUBBLE, do not throw. Throwing aborts the whole
          // command, which discards the siblings that had already found clean
          // slots: measured on a live FEVER review, the Reviewer produced 10
          // findings and only 2 survived, because one over-full bubble took its
          // whole batch down with it. The full page is a property of THIS
          // bubble, not of the request, so it gets the same soft skip a lost
          // anchor gets a hundred lines above, and the findings that do fit
          // still reach the reviewer.
          //
          // The page is still never covered: nothing above MAX_TOLERABLE_COVERAGE
          // is drawn either way. The change is what happens to the others.
          skipped.push(
            `"${String((el as { text?: string }).text ?? '').slice(0, 60)}…" on page ${page.index + 1}: ${placedFrames.length} bubble(s) already sit there and the best remaining slot would cover ${Math.round(clear.coverage * 100)}% of the drawing, so it was left off rather than drawn over the artwork. Put this finding in the synthesis, or shorten it so its bubble is smaller. Do not retry the same text.`
          );
          continue;
        }
        const frame = clear?.frame ?? resolveNoteCollisions(sized, placedFrames, page.width, page.height);
        if (clear) {
          console.info(
            `[Slides] note bubble on page ${page.index + 1} → (${Math.round(frame.x)},${Math.round(frame.y)} ${Math.round(frame.w)}x${Math.round(frame.h)}), covers ${(clear.coverage * 100).toFixed(1)}% drawing, ${placedFrames.length} already placed`
          );
        }
        placedFrames.push(frame);
        guarded.push({ ...el, frame });
      }

      options?.abortSignal?.throwIfAborted();
      if (reviewRun && useSlidesStore.getState().getReviewRun(reviewRun.sessionId)?.id !== reviewRun.id) {
        throw new Error('This review was cleared or superseded. No annotations were added.');
      }

      // Be on the page BEFORE the first bubble lands, not after the last one:
      // the point is to watch the review being made, and jumping afterwards
      // shows only the finished result.
      if (st.autoFollowAgent) st.focusPage(deck.projectId, page.id);
      // Done looking, starting to write: stop the reading pulse so the two
      // states never overlap.
      st.setReadingPage(null);
      // Provenance: Reviewer-authored (phase-2 learning measures what humans correct).
      st.withAgentMutation(() => guarded.forEach((el) => st.addElement(deck.projectId, page.id, { ...el, origin: options?.isAgentGenerated === false ? 'user' : 'agent' })), options?.isAgentGenerated);
      // Queue the callouts to be written out one after another. Order is the
      // order the reviewer wrote them, which is what makes it read as a person
      // working down the sheet rather than a batch landing at once.
      const typed: string[] = [];
      if (st.autoFollowAgent) {
        guarded.forEach((el) => {
          if (el.type === 'note' && (el as { text?: string }).text) {
            st.startAgentTyping(el.id);
            typed.push(el.id);
          }
        });
      }
      st.markAgentActive();
      // Follow-the-agent UX (Analyst parity): the cursor glides to the last
      // placed annotation, and the editor jumps to the page being annotated.
      const last = guarded[guarded.length - 1];
      if (last) {
        st.setAgentEdits({ projectId: deck.projectId, pageId: page.id, frame: last.frame, elementId: last.id, at: Date.now() });
      }
      // HOLD THE SCRIPT until the callouts have actually been written on screen.
      // Without this the command returned the instant the elements were in the
      // document, the agent moved to the next page, and the animation either
      // played to a page the reviewer had already been navigated away from or
      // never ran at all - a queued note renders as nothing until its turn, so
      // the notes simply materialised later. The reviewer asked to WATCH the
      // review being written; that only works if the writing paces the script.
      if (typed.length > 0) await useSlidesStore.getState().waitForAgentTyping();
      if (guarded.length > 0) {
        toast.success(
          `Placed ${guarded.length} annotation${guarded.length === 1 ? '' : 's'} on page ${page.index + 1}` +
            (skipped.length > 0 ? `, skipped ${skipped.length}` : '')
        );
      }

      // Reported only AFTER the survivors are on the page. The agent learns what
      // it must re-word, and the callouts it got right are already placed and
      // paid for, so the retry is one element rather than the whole batch. The
      // "do not resend" is load-bearing: without it the agent re-sends the lot
      // and the page ends up with duplicates of everything that worked.
      if (skipped.length > 0) {
        const placed = guarded.length;
        throw new Error(
          `SLIDES_ADD_ELEMENT: placed ${placed} callout(s) on page ${page.index + 1}` +
            ` and could not place ${skipped.length}: ${skipped.join('; ')}.` +
            ` The ${placed} that landed are already on the page - do NOT resend them.` +
            ` Act ONLY on the failed one(s), and read each reason.` +
            ` AN ANCHOR THAT COULD NOT BE FOUND is worth one more try with a different anchorElement` +
            ` naming a clearly visible object.` +
            ` A PAGE WITH NO ROOM LEFT IS FINAL: that page is full, and it stays full however you` +
            ` re-word the note - shortening the text does not help, because the bubble has a minimum` +
            ` width and the space that is left is on the drawing. Do NOT attempt another callout on` +
            ` that page this review. Carry the finding into the synthesis instead, where it still` +
            ` reaches the tenant, and move on to the next page. Re-trying it costs a turn and loses` +
            ` the finding anyway.`
        );
      }
    },
  },

  SLIDES_UPDATE_ELEMENT: {
    action_type: 'SLIDES_UPDATE_ELEMENT',
    traceLabels: { active: 'editing', done: 'edited', unit: 'element' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Edits an existing overlay element on a page (move/resize via "frame", reword via "text", recolor, change severity…). Element ids are listed in your live deck context.',
    parameters: [
      { name: 'page', type: 'number', description: '1-based page number (or a page id string).', required: true },
      { name: 'elementId', type: 'string', description: 'The id of the element to edit.', required: true },
      {
        name: 'patch',
        type: 'object',
        description: 'Fields to change, e.g. { text: "...", frame: {x,y,w,h}, severity: "fail", anchorText: "ZONE CAISSES" }.',
        required: true,
      },
    ],
    availableInModes: ['slides'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const deck = requireDeck('SLIDES_UPDATE_ELEMENT', options?.projectId);
      const reviewRun = options?.sessionId ? useSlidesStore.getState().getReviewRun(options.sessionId) : null;
      const page = resolvePage(deck, params.page, 'SLIDES_UPDATE_ELEMENT');
      const elementId = typeof params.elementId === 'string' ? params.elementId.trim() : '';
      if (!elementId) throw new Error('SLIDES_UPDATE_ELEMENT: "elementId" is required.');
      const patchRaw = (params.patch ?? {}) as Record<string, unknown>;
      const patch = buildElementPatch(patchRaw, 'SLIDES_UPDATE_ELEMENT');
      // anchorElement → a point found visually, through the same resolver the
      // ADD path uses. Both anchor fields are TRANSIENT: they name a target and
      // are never stored. Until now UPDATE accepted anchorElement, validated it,
      // and then did nothing with it - so it fell through to updateElement and
      // was persisted verbatim onto the element while the leader line stayed
      // exactly where it was. The command reported success on a no-op.
      const patchAnchorElement = (patch as { anchorElement?: string }).anchorElement;
      const existingEl = page.elements.find((e) => e.id === elementId);
      let patchGrounded = false;
      if (patchAnchorElement !== undefined) {
        delete (patch as { anchorElement?: string }).anchorElement;
        // ponytail: re-anchoring an EXISTING callout pays the full annotation
        // base again, though the deck still shows one annotation. Same upgrade
        // path as the batch case above: price on placement, not on locate.
        const located = await locateAnchorForElement(
          page,
          patchAnchorElement,
          (patch as { text?: string }).text ?? (existingEl as { text?: string } | undefined)?.text,
          (patch as { anchor?: { x: number; y: number } }).anchor ??
            (existingEl as { anchor?: { x: number; y: number } } | undefined)?.anchor,
          deck.projectId
        );
        if (located.confidence === 'lost') {
          throw new Error(
            `SLIDES_UPDATE_ELEMENT: could not find "${patchAnchorElement}" on page ${page.index + 1} - the locator checked ${located.attempts} candidate point(s) and ruled that none of them is on it; the best one is on "${located.whatIsThere}". Name a different object, or use anchorText if a label sits on it.`
          );
        }
        (patch as { anchor?: { x: number; y: number } }).anchor = { x: located.x, y: located.y };
        stampLocateCredits(
          patch as { credits?: number },
          located,
          (existingEl as { credits?: number } | undefined)?.credits,
        );
        patchGrounded = located.confidence === 'verified';
        console.info(
          `[Slides] UPDATE anchorElement "${patchAnchorElement}" → (${Math.round(located.x)},${Math.round(located.y)}) in region P${located.region} on page ${page.index + 1}, ${located.confidence} after ${located.attempts} candidate(s): ${located.whatIsThere}`
        );
      }
      // anchorText → exact label coordinates (deterministic; transient field).
      const patchAnchorText = (patch as { anchorText?: string }).anchorText;
      if (patchAnchorText !== undefined) {
        delete (patch as { anchorText?: string }).anchorText;
        const hit = resolveAnchorText(page.textMap, patchAnchorText);
        if (!hit) {
          throw new Error(
            `SLIDES_UPDATE_ELEMENT: anchorText "${patchAnchorText}" matches no label on page ${page.index + 1}. Use a label from TEXT ANCHORS or a numeric anchor {x,y}.`
          );
        }
        (patch as { anchor?: { x: number; y: number } }).anchor = hit;
      }
      if ((patch as { anchor?: { x: number; y: number } }).anchor) {
        const clamped = clampAnchorToPage(
          (patch as { anchor: { x: number; y: number } }).anchor, page.width, page.height
        );
        // Same rule as the ADD path: a verified point was confirmed to be on the
        // element inside an enlarged crop, so the ink probe must not second-guess
        // it. Everything else gets the empty-paper check.
        if (patchGrounded) {
          (patch as { anchor?: { x: number; y: number } }).anchor = clamped;
        } else {
          const snapped = await snapAnchorToInk(page, clamped);
          if (snapped === null) {
            throw new Error(
              `SLIDES_UPDATE_ELEMENT: anchor (${Math.round(clamped.x)},${Math.round(clamped.y)}) is far from any drawn element (empty paper). Aim at the element (anchorText from TEXT ANCHORS, or re-read the grid).`
            );
          }
          (patch as { anchor?: { x: number; y: number } }).anchor = snapped;
        }
      }
      // Three-value colour code: 'warn' notes render blue, never orange.
      if ((patch as { severity?: string }).severity === 'warn') {
        (patch as { severity?: 'info' | 'warn' | 'fail' }).severity = 'info';
      }
      if (Object.keys(patch).length === 0) {
        throw new Error('SLIDES_UPDATE_ELEMENT: "patch" contained no editable fields.');
      }
      // Exact-fit box on note edits: when the text or frame of a note changes,
      // the app recomputes the bubble size from the (new) text.
      const patchText = (patch as { text?: string }).text;
      if (existingEl?.type === 'note' && (patchText !== undefined || patch.frame !== undefined)) {
        const nextText = patchText ?? existingEl.text ?? '';
        const nextFrame = (patch.frame as { x: number; y: number; w: number; h: number } | undefined) ?? existingEl.frame;
        const layout = buildNoteLayout({ ...existingEl, ...patch, text: nextText, frame: nextFrame }, page.width, page.height);
        if (layout.overflow || layout.frame.w < 20) {
          patch.frame = nextFrame;
        } else {
        const size = layout.frame;
        const blockers = noteBlockers(deck, page, elementId);
        // The SAME clear-space search as placement, not just the overlap guard.
        // Consolidating findings is an UPDATE ("replace these two comments with
        // one"), and a longer text means a taller bubble - so re-laying an edited
        // note with the weaker rule is exactly how a page that was clean drifts
        // back onto the drawing.
        const anchorForLayout =
          (patch as { anchor?: { x: number; y: number } }).anchor ??
          (existingEl as { anchor?: { x: number; y: number } }).anchor ?? {
            x: nextFrame.x,
            y: nextFrame.y,
          };
        const clear = await placeNoteInClearSpace(
          page,
          size,
          anchorForLayout,
          blockers,
          page.width,
          page.height
        );
        (patch as { frame?: { x: number; y: number; w: number; h: number } }).frame =
          clear && clear.coverage <= MAX_TOLERABLE_COVERAGE
            ? clear.frame
            : resolveNoteCollisions(
                { x: nextFrame.x, y: nextFrame.y, w: size.w, h: size.h },
                blockers,
                page.width,
                page.height
              );
        }
      }
      options?.abortSignal?.throwIfAborted();
      if (reviewRun && useSlidesStore.getState().getReviewRun(reviewRun.sessionId)?.id !== reviewRun.id) {
        throw new Error('This review was stopped or superseded. No annotations were changed.');
      }
      {
        // Be on the page before the edit lands, for the same reason as placement.
        const st = useSlidesStore.getState();
        if (st.autoFollowAgent) st.focusPage(deck.projectId, page.id);
      }
      let retyped = false;
      useSlidesStore.getState().withAgentMutation(() => useSlidesStore.getState().updateElement(deck.projectId, page.id, elementId, patch), options?.isAgentGenerated);
      {
        // Follow-the-agent UX: glide the cursor to the edited element.
        const st = useSlidesStore.getState();
        const frame = (patch as { frame?: { x: number; y: number; w: number; h: number } }).frame ?? existingEl?.frame;
        if (frame) {
          st.setAgentEdits({ projectId: deck.projectId, pageId: page.id, frame, elementId, at: Date.now() });
        }
        // Re-typing a REWORDED comment: consolidating two findings into one is
        // an update, and it should read as the reviewer rewriting the note, not
        // as the text silently swapping.
        if (st.autoFollowAgent && existingEl?.type === 'note' && patchText !== undefined && patchText) {
          st.startAgentTyping(elementId);
          retyped = true;
        }
      }
      if (retyped) await useSlidesStore.getState().waitForAgentTyping();
      useSlidesStore.getState().markAgentActive();
    },
  },

  SLIDES_REMOVE_ELEMENT: {
    action_type: 'SLIDES_REMOVE_ELEMENT',
    traceLabels: { active: 'removing', done: 'removed', unit: 'element' },
    targetEntityType: 'ABSOLUTE',
    description: 'Removes an overlay element from a page of the open deck.',
    parameters: [
      { name: 'page', type: 'number', description: '1-based page number (or a page id string).', required: true },
      { name: 'elementId', type: 'string', description: 'The id of the element to remove.', required: true },
    ],
    availableInModes: ['slides'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const deck = requireDeck('SLIDES_REMOVE_ELEMENT', options?.projectId);
      const page = resolvePage(deck, params.page, 'SLIDES_REMOVE_ELEMENT');
      const elementId = typeof params.elementId === 'string' ? params.elementId.trim() : '';
      if (!elementId) throw new Error('SLIDES_REMOVE_ELEMENT: "elementId" is required.');
      useSlidesStore.getState().withAgentMutation(() => useSlidesStore.getState().removeElement(deck.projectId, page.id, elementId), options?.isAgentGenerated);
    },
  },

  SLIDES_UPDATE_PAGE: {
    action_type: 'SLIDES_UPDATE_PAGE',
    traceLabels: { active: 'labeling', done: 'labeled', unit: 'page' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Sets a page\'s label - what the graphic piece IS ("Floor plan", "Facade elevation - projected", "Render - entrance"…). Label every page during your inventory pass.',
    parameters: [
      { name: 'page', type: 'number', description: '1-based page number (or a page id string).', required: true },
      { name: 'label', type: 'string', description: 'The page label.', required: true },
    ],
    availableInModes: ['slides'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const deck = requireDeck('SLIDES_UPDATE_PAGE', options?.projectId);
      const page = resolvePage(deck, params.page, 'SLIDES_UPDATE_PAGE');
      const label = typeof params.label === 'string' ? params.label.trim() : '';
      if (!label) throw new Error('SLIDES_UPDATE_PAGE: "label" is required.');
      useSlidesStore.getState().withAgentMutation(() => useSlidesStore.getState().setPageLabel(deck.projectId, page.id, label), options?.isAgentGenerated);
    },
  },

  SLIDES_ADD_PAGE: {
    action_type: 'SLIDES_ADD_PAGE',
    traceLabels: { active: 'adding', done: 'added', unit: 'page' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Inserts a blank page (e.g. a review-synthesis page you then compose with text/stamp elements). Appends unless you pass "atPosition". Size: pass a "preset" ("16:9" = 960x540, "4:3", "A4_landscape" = 842x595, "A4_portrait", "letter_landscape") or explicit width/height - MATCH THE DECK\'S EXISTING PAGES, since a deck imported from A4 will otherwise get a 16:9 page appended to it. Defaults to 16:9.',
    parameters: [
      { name: 'label', type: 'string', description: 'Optional page label.', required: false },
      {
        name: 'preset',
        type: 'string',
        description: 'Page size preset: 16:9 | 4:3 | A4_landscape | A4_portrait | letter_landscape.',
        required: false,
      },
      { name: 'width', type: 'number', description: 'Page width in points (default 960).', required: false },
      { name: 'height', type: 'number', description: 'Page height in points (default 540).', required: false },
      { name: 'background', type: 'string', description: 'Hex page background (default white).', required: false },
      {
        name: 'atPosition',
        type: 'number',
        description: '1-based position to insert at. Omit to append.',
        required: false,
      },
    ],
    availableInModes: ['slides'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const deck = requireDeck('SLIDES_ADD_PAGE', options?.projectId);
      const st = useSlidesStore.getState();
      let preset = PAGE_SIZE_PRESETS['16:9'];
      if (params.preset !== undefined) {
        const found = PAGE_SIZE_PRESETS[String(params.preset)];
        if (!found) {
          throw new Error(
            `SLIDES_ADD_PAGE: unknown preset "${String(params.preset)}". Use ${Object.keys(PAGE_SIZE_PRESETS).join(' | ')}.`
          );
        }
        preset = found;
      }
      const atPosition =
        typeof params.atPosition === 'number' && Number.isFinite(params.atPosition)
          ? Math.round(params.atPosition) - 1
          : undefined;
      const page = st.withAgentMutation(() => st.addPage(
        deck.projectId,
        {
          label: typeof params.label === 'string' && params.label.trim() ? params.label.trim() : undefined,
          width: typeof params.width === 'number' && params.width > 0 ? params.width : preset!.width,
          height: typeof params.height === 'number' && params.height > 0 ? params.height : preset!.height,
          background:
            typeof params.background === 'string' && params.background.trim()
              ? params.background.trim()
              : undefined,
        },
        atPosition
      ), options?.isAgentGenerated);
      if (page) st.focusPage(deck.projectId, page.id);
      st.markAgentActive();
    },
  },

  SLIDES_PROPOSE_VISUAL: {
    action_type: 'SLIDES_PROPOSE_VISUAL',
    traceLabels: { active: 'proposing', done: 'proposed', unit: 'proposal' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Builds the "level of ambition" COUNTER-PROPOSAL as TWO full-page sheets, the way the RDM team marks up a proposal by hand. Sheet 1 is the tenant\'s own render with your remarks DRAWN ON IT in blue pen - an arrow or a circle to each element, a few handwritten words about it. Sheet 2 is the corrected design, generated from that annotated image so the model is shown where each correction goes rather than told. Use it ONCE per review, after your structural findings exist. The region must contain the render ITSELF, preferring a 3D view or perspective over a flat facade elevation (take the elevation only when the deck has no 3D view), never a plan, a section or a location map - the model edits the pixels you hand it. Give the region in PAGE POINTS from the coordinate grid on the page. "changes" are the corrections, physical and specific, taken from findings you already made - each one becomes a handwritten note, so keep each to a few words and write them in the language of the review. Both sheets receive a Westfield cartouche automatically. The remarks live in the drawing, so do not add extra callouts or title blocks. Each pass is generated, judged by an independent critic and regenerated up to three times, so it takes a couple of minutes and spends credits on several image generations - propose it for approval rather than doing it speculatively. It returns {croquis, proposal}, each with {attempts, approved, judged, remainingProblems}: when "approved" is false the sheet still carries the best draft, and you MUST say so and quote remainingProblems rather than presenting it as validated.',
    parameters: [
      { name: 'page', type: 'number', description: '1-based page number carrying the storefront to re-render.', required: true },
      { name: 'region', type: 'object', description: 'Crop rectangle in PAGE POINTS: { x, y, w, h }, top-left origin. Frame the shopfront generously - include the whole unit width and its full height.', required: true },
      { name: 'changes', type: 'array', description: 'The corrections to apply, one per entry, physical and specific - and EXHAUSTIVE about where, WITHOUT asking for more than the guideline requires. Two failure modes, both measured. Too vague: "remove the vinyl graphics" comes back with one bay cleared and the other untouched, so name every place the change applies ("remove the applied vinyl from the glazing of BOTH lateral bays: the smiley panel on the left and the yellow banner on the right"). Too greedy: asking to "clear the bays of all opaque backdrops" demands the tenant empty their window displays, which no guideline says and no draft can satisfy - three runs were rejected against that bar. Correct the SHOPFRONT (glazing, applied graphics, closure, materials, signage), never the merchandising behind it. Say what the result should LOOK like, not which rule it satisfies.', required: true },
      { name: 'label', type: 'string', description: 'Label for the ANNOTATED sheet. Defaults to "Remarques". The second sheet is always labelled "Concept Studio proposal".', required: false },
    ],
    // Without this the command is in the registry and in no prompt: getCommandsForMode
    // filters on `availableInModes ?? []`, so an omitted list hides the tool from every
    // agent. It shipped omitted, which is why the Reviewer answered "I cannot generate
    // imagery" and then drew its own imitation storefront instead.
    availableInModes: ['slides'],
    headlessCompatible: false,
    // The only slides command that reaches the kernel: the proposal runs on the
    // Studio generation rails, which live there.
    handler: async (params, kernel, options) => {
      const deck = requireDeck('SLIDES_PROPOSE_VISUAL', options?.projectId);
      const source = resolvePage(deck, params.page, 'SLIDES_PROPOSE_VISUAL');
      const layout = proposalSheetLayout(source.width, source.height);
      const st = useSlidesStore.getState();

      const region = params.region as { x?: number; y?: number; w?: number; h?: number } | undefined;
      if (!region || ![region.x, region.y, region.w, region.h].every((v) => typeof v === 'number' && Number.isFinite(v))) {
        throw new Error('SLIDES_PROPOSE_VISUAL: region must be { x, y, w, h } in page points.');
      }
      if (!source.snapshotUrl) {
        throw new Error(
          `SLIDES_PROPOSE_VISUAL: page ${source.index + 1} has no rendered snapshot, so there is nothing to re-render. Pick a page imported from the PDF.`
        );
      }
      const changes = Array.isArray(params.changes)
        ? (params.changes as unknown[]).filter((c): c is string => typeof c === 'string' && c.trim().length > 0)
        : [];
      if (changes.length === 0) {
        throw new Error(
          'SLIDES_PROPOSE_VISUAL: "changes" is required - the proposal is built FROM your findings. Pass the physical corrections you already identified.'
        );
      }

      const sessionId = options?.sessionId || kernel.current?.getActiveChatSessionId?.();
      if (!sessionId) throw new Error('SLIDES_PROPOSE_VISUAL: a Slides session is required.');
      const previousRun = st.getProposalRun(sessionId);
      const parentReview = options?.isAgentGenerated ? st.getReviewRun(sessionId) : null;
      const run = previousRun ?? st.beginProposalRun(deck.projectId, sessionId, parentReview?.id);
      if (!run || run.projectId !== deck.projectId || run.round !== deck.reviewRound || run.status !== 'running') {
        throw new Error('SLIDES_PROPOSE_VISUAL: stop the current workflow before starting another counter-proposal.');
      }
      const assertCurrent = () => {
        options?.abortSignal?.throwIfAborted();
        if (useSlidesStore.getState().getProposalRun(sessionId)?.id !== run.id) {
          throw new DOMException('This counter-proposal was stopped or superseded.', 'AbortError');
        }
      };
      const stop = () => { void st.finishProposalRun(run, 'stopped'); };
      options?.abortSignal?.addEventListener('abort', stop, { once: true });
      try {
      assertCurrent();
      st.updateProposalProgress(run, { phase: 'preparing' });
      if (!await st.saveDeckNow(deck.projectId)) {
        throw new Error('SLIDES_PROPOSE_VISUAL: the input could not be saved. Resolve the save error before generating.');
      }
      assertCurrent();

      // 1. Cut the storefront out of the page. The crop is the BEFORE image and
      //    the model's input, so the tenant is shown exactly what was edited.
      // Same reasoning as the critic call: a bounced crop loses the proposal.
      const projectName = kernel.current?.getInventory?.().Project.registry[deck.projectId]?.name
        || st.deckIndex.find((item) => item.projectId === deck.projectId)?.name;
      const deckName = projectName?.trim() || (typeof deck.sourceFileName === 'string' && deck.sourceFileName.trim()
        ? deck.sourceFileName.trim().replace(/\.pdf$/i, '')
        : 'Design review');
      const cartouche = { projectName: deckName, reviewRound: deck.reviewRound, createdAt: new Date().toISOString() };
      const cropRes = await fetchApi('/api/slides/counter-proposal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          snapshotUrl: source.snapshotUrl,
          pageW: source.width,
          cartouche,
          region: { x: region.x, y: region.y, w: region.w, h: region.h },
        }),
        signal: options?.abortSignal,
      });
      if (!cropRes.ok) {
        const payload = await cropRes.json().catch(() => null);
        throw new Error(`SLIDES_PROPOSE_VISUAL: ${payload?.message || `crop failed (${cropRes.status})`}`);
      }
      // The crop's real pixel size comes back with it. It is not decoration: the
      // generation FRAME is what the model is shown, a fresh workspace opens
      // square (720x720), and the frame is sized by canvas placement, not by the
      // layer that lands in it. Measured: attempt 1 of a loop ran through a
      // square window over a 1332x751 shopfront and came back barely altered,
      // while later attempts - whose workspaces inherited a frame from the
      // previous one - were real edits. So the shape is passed EXPLICITLY here,
      // from the server's own measurement, rather than inferred from a layer
      // whose rect is not populated yet at that moment.
      const {
        beforeUrl,
        width: cropW,
        height: cropH,
        cartoucheUrls,
      } = (await cropRes.json()) as {
        beforeUrl: string; width?: number; height?: number;
        cartoucheUrls?: { croquis?: string; proposal?: string };
      };
      if (!cartoucheUrls?.croquis?.startsWith('/api/assets/') || !cartoucheUrls?.proposal?.startsWith('/api/assets/')) {
        throw new Error('SLIDES_PROPOSE_VISUAL: the Westfield cartouches could not be prepared. No generation was started.');
      }
      assertCurrent();
      const cropAspect = cropW && cropH && cropH > 0 ? `${cropW}:${cropH}` : null;

      // 2. TWO PASSES, each generated and then judged by a critic that did not
      //    write its prompt.
      //
      //    Pass 1 marks up the tenant's own render by hand, in blue pen, the way
      //    the RDM team actually reviews a drawing: an arrow to the thing, two
      //    words about it. Pass 2 takes THAT annotated image and produces the
      //    corrected design from it.
      //
      //    Feeding the annotated image forward is the point of the split. Prose
      //    says WHAT to change and leaves the model to find the thing; a pen mark
      //    says WHERE. The corrections are still passed as words - the marks
      //    carry position, the words carry meaning - but the model no longer has
      //    to re-derive "the dividers" from a sentence.
      //
      //    The generation runs on the shared Studio rails (lib/studio/
      //    runStudioGeneration), the same sequence Master's RUN_STUDIO_STEP
      //    executes: a real project, a real workspace, a real version per draft,
      //    a real GenerationLog. Drafts live in a COMPANION Studio project rather
      //    than the deck's own SLIDES project: a deck is a document, and hanging
      //    canvas workspaces off it would put two different editors on one
      //    project.
      const k = kernel?.current;
      if (!k) throw new Error('SLIDES_PROPOSE_VISUAL: Kernel not available.');

      let studioProjectId = deck.doc.proposalProjectId ?? null;
      const allWorkspaces: string[] = [];

      /**
       * One judged pass: generate, ask the critic, fold its guidance into the
       * next attempt, keep the best SENDABLE draft.
       *
       * Two callers - the croquis and the applied design - and they differ in
       * three things: what they generate from, the instruction, and which
       * question the critic is asked. Everything else, including the bookkeeping
       * that stops a failure leaking workspaces, is identical, which is why this
       * is one function rather than the loop written twice.
       */
      const runJudgedPass = async (pass: {
        sourceUrl: string;
        buildPrompt: (changes: string[], drawingNotes?: string[]) => string;
        /**
         * Where the critic's guidance goes on a retry. The design pass folds it
         * into the corrections, because there it IS the instruction. The croquis
         * pass must not: its "changes" become handwriting, so folding a critic's
         * note in writes that note on the sheet in blue pen.
         */
        guidanceIsDrawingNote?: boolean;
        criticMode: 'design' | 'croquis';
        aspectRatio: string | null;
        /**
         * Keep only what the model ADDED, over this image. Set for the croquis
         * pass, whose model regenerates the render it is asked to annotate.
         */
        compositeInkOnto?: string;
      }): Promise<{ url: string; verdict: ProposalVerdict | null; attempts: number; workspaceId?: string; stoppedReason?: string }> => {
        let attemptChanges = changes;
        let drawingNotes: string[] = [];
        let best: { url: string; verdict: ProposalVerdict | null; workspaceId?: string } | null = null;
        const drafts: { url: string; verdict: ProposalVerdict | null; workspaceId: string }[] = [];
        let generationError: unknown = null;

        try {
          for (let attempt = 1; attempt <= MAX_PROPOSAL_ATTEMPTS; attempt++) {
            assertCurrent();
            st.updateProposalProgress(run, { phase: 'generating', sheet: pass.criticMode === 'croquis' ? 1 : 2, attempt });
            const generation = await runStudioGeneration(k, {
              projectId: studioProjectId,
              projectName: `${deckName}, proposals`.slice(0, 60),
              sourceImageUrl: pass.sourceUrl,
              referenceImageUrls: pass.criticMode === 'design' && deck.doc.brandResearch?.status !== 'invalidated'
                ? deck.doc.brandResearch?.references.map((r) => r.assetUrl) : [],
              prompt: pass.buildPrompt(attemptChanges, drawingNotes),
              model: COUNTER_PROPOSAL_MODEL,
              aspectRatio: pass.aspectRatio,
              // Search grounding OFF. Handed a shopfront with the tenant's name
              // across it, the model looks the brand up and reconstructs the shop
              // it finds instead of applying the review.
              //
              // 2K, not the model's 1K default: the pair is printed on an A0
              // sheet, and the critic cannot police lettering it cannot read - a
              // draft once passed carrying "A MUL TIENSISON EXPERIENCE" on a
              // plinth, eight pixels tall in the output.
              modelParams: { enable_google_search: false, imageSize: '2K' },
              // Every attempt starts from the pass's own source, never from the
              // previous attempt: sharing a workspace makes draft 2 an edit of
              // draft 1 with a second copy of the source stacked on it.
              freshWorkspace: true,
              abortSignal: options?.abortSignal,
            });
            assertCurrent();
            if (generation.projectId !== studioProjectId) {
              studioProjectId = generation.projectId;
              st.withAgentMutation(() => st.applyDocMutation(deck.projectId, (doc) => ({
                ...doc,
                proposalProjectId: generation.projectId,
              })), options?.isAgentGenerated);
            }
            allWorkspaces.push(generation.workspaceId);
            if (!generation.outputImageUrl) {
              throw new Error('SLIDES_PROPOSE_VISUAL: the generation produced no image.');
            }

            // Lift the ink onto the untouched original. A failure here is not
            // fatal: the model's own output is still a usable draft, it just
            // carries whatever the re-render changed, which the critic then
            // judges as it would any other draft.
            let draftUrl = generation.outputImageUrl;
            st.updateProposalProgress(run, { phase: 'checking', sheet: pass.criticMode === 'croquis' ? 1 : 2, attempt });
            if (pass.compositeInkOnto) {
              assertCurrent();
              try {
                const res = await fetchApi('/api/slides/croquis-composite', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ baseUrl: pass.compositeInkOnto, drawnUrl: draftUrl }),
                  signal: options?.abortSignal,
                });
                if (res.ok) {
                  const composited = (await res.json()) as { croquisUrl?: string };
                  if (composited.croquisUrl) draftUrl = composited.croquisUrl;
                }
              } catch {
                /* keep the raw generation */
              }
            }

            // Judged against the ORIGINAL findings every time, never against the
            // refined instruction: the brief is what the review asked for, and
            // grading attempt three against attempt three's own wording is how a
            // loop converges on something nobody wanted.
            assertCurrent();
            const verdict = await critiqueProposal(
              pass.sourceUrl,
              draftUrl,
              changes,
              deck.projectId,
              pass.criticMode,
              options?.abortSignal
            );
            assertCurrent();
            drafts.push({ url: draftUrl, verdict, workspaceId: generation.workspaceId });

            // Only a SENDABLE draft can be placed. One that changed the unit or
            // carries corrupted handwriting is not a weaker option to fall back
            // on. An UNJUDGED draft (critic unreachable) stays eligible: nobody
            // condemned it, and the agent is told it was never checked.
            const placeable = verdict === null || verdict.sendable;
            if (placeable && (!best || (verdict?.score ?? 0) > (best.verdict?.score ?? -1))) {
              best = { url: draftUrl, verdict, workspaceId: generation.workspaceId };
            }
            if (verdict?.satisfied) break;
            if (!verdict) break;
            if (attempt < MAX_PROPOSAL_ATTEMPTS) {
              if (pass.guidanceIsDrawingNote) {
                drawingNotes = [verdict.guidance, ...verdict.problems].filter(Boolean).slice(0, 4);
              } else {
                attemptChanges = refineChanges(changes, verdict);
              }
            }
          }
        } catch (error) {
          // Held: an earlier attempt may have produced something worth keeping,
          // and the caller still has to clean up. Rethrown below if not.
          generationError = error;
        }

        assertCurrent();

        if (!best) {
          if (generationError) throw generationError;
          const reasons = drafts
            .flatMap((d) => d.verdict?.problems ?? [])
            .slice(0, 3)
            .join(' | ');
          throw new Error(
            `SLIDES_PROPOSE_VISUAL: ${drafts.length} ${pass.criticMode} draft(s) generated, none fit to send. ${reasons || 'The critic rejected every attempt.'} Re-word "changes" as physical, visible corrections and try again.`
          );
        }
        return {
          url: best.url, verdict: best.verdict, attempts: drafts.length, workspaceId: best.workspaceId,
          stoppedReason: generationError ? (generationError instanceof Error ? generationError.message : String(generationError)) : undefined,
        };
      };

      // Bin the canvases this run generated, except the ones holding what is
      // placed. Deleting a workspace is non-destructive - the layer records
      // survive in the registry - so rejected drafts stay recoverable while the
      // proposals project stays legible. Without this a deck reviewed a few
      // times left it holding dozens of overlapping workspaces. Kept by
      // identity, not position: the best draft is not always the last attempt.
      const binDrafts = async (keep: ReadonlySet<string | undefined>) => {
        const binned = allWorkspaces.splice(0).filter((workspaceId) => !keep.has(workspaceId));
        if (!binned.length || !studioProjectId) return;
        // A companion project this run just created has a first canvas the server made and the
        // browser never loaded, so the kernel counts one canvas and refuses to delete what it takes
        // for the project's last. Load them first.
        const project = await k.syncEntity?.('Project', studioProjectId)?.catch(() => null);
        const canvasIds = (project ?? k.getInventory?.().Project.registry[studioProjectId])?.workspaceIds;
        if (Array.isArray(canvasIds) && canvasIds.length) await k.ensureInventoryIds?.('StudioWorkspace', canvasIds)?.catch(() => undefined);
        for (const workspaceId of binned) {
          try {
            k.deleteWorkspace(studioProjectId, workspaceId);
          } catch {
            // A leftover workspace is untidy, never a reason to lose a proposal
            // that has already been generated and paid for.
          }
        }
      };

      let croquis!: Awaited<ReturnType<typeof runJudgedPass>>;
      let applied!: Awaited<ReturnType<typeof runJudgedPass>>;
      try {
        // PASS 1: the reviewer's remarks, drawn on the tenant's own render.
        //
        //    The model cannot annotate an image, it regenerates one, so its output
        //    is a COPY of the render with the marks on it. The copy is never quite
        //    the original: the first live run came back with the tenant's banner
        //    redrawn from "Step into a world of joy" to "Stop into a world of jay",
        //    and the critic correctly refused all three drafts, because this sheet
        //    is captioned as their own submission.
        //
        //    So the ink is composited back onto the real crop before anything else
        //    happens to it. The render underneath is then genuinely theirs, and the
        //    "did it alter the render" question stops being one the critic has to
        //    police - it cannot happen.
        croquis = await runJudgedPass({
          sourceUrl: beforeUrl,
          buildPrompt: buildCroquisPrompt,
          criticMode: 'croquis',
          aspectRatio: cropAspect,
          compositeInkOnto: beforeUrl,
          guidanceIsDrawingNote: true,
        });

        // PASS 2: the corrected design, generated FROM the annotated image.
        assertCurrent();
        applied = await runJudgedPass({
          sourceUrl: croquis.url,
          buildPrompt: (changes) => buildAppliedFromCroquisPrompt(changes, deck.doc.brandResearch?.status !== 'invalidated' ? deck.doc.brandResearch?.brief : undefined),
          criticMode: 'design',
          aspectRatio: cropAspect,
        });
        assertCurrent();
      } catch (error) {
        // A pass with no sendable draft, a failed generation or a Stop places
        // nothing, so every canvas it made is an orphan.
        await binDrafts(new Set());
        throw error;
      }
      st.updateProposalProgress(run, { phase: 'assembling' });
      await binDrafts(new Set([croquis.workspaceId, applied.workspaceId]));

      // 3. TWO sheets, each with its own durable Westfield cartouche. Keep the
      // original generated assets untouched; branding only changes placement.
      const W = source.width;
      const H = source.height;
      const croquisLabel =
        typeof params.label === 'string' && params.label.trim()
          ? params.label.trim()
          : 'Remarques';
      const sheets = [
        { url: croquis.url, label: croquisLabel, cartoucheUrl: cartoucheUrls.croquis },
        { url: applied.url, label: 'Concept Studio proposal', cartoucheUrl: cartoucheUrls.proposal },
      ];
      const measureSignal = AbortSignal.any([
        ...(options?.abortSignal ? [options.abortSignal] : []), AbortSignal.timeout(20_000),
      ]);
      // Measure BOTH before appending either page. A missing asset or Stop must
      // not leave a half-finished pair, nor silently distort a storefront.
      const prepared = await Promise.all(sheets.map(async (sheet) => {
        const measured = await new Promise<{ w: number; h: number }>((resolve, reject) => {
          measureSignal.throwIfAborted();
          const probe = new Image();
          const cleanup = () => {
            measureSignal.removeEventListener('abort', abort);
            probe.onload = null;
            probe.onerror = null;
          };
          const abort = () => { cleanup(); reject(measureSignal.reason); };
          measureSignal.addEventListener('abort', abort, { once: true });
          probe.onload = () => {
            cleanup();
            if (probe.naturalWidth > 0 && probe.naturalHeight > 0) {
              resolve({ w: probe.naturalWidth, h: probe.naturalHeight });
            } else reject(new Error('The proposal image has no readable dimensions.'));
          };
          probe.onerror = () => { cleanup(); reject(new Error('The proposal image could not be loaded.')); };
          probe.src = sheet.url;
        });
        return { ...sheet, frame: fitInBox(measured.w, measured.h, layout.image) };
      }));
      assertCurrent();

      const recordedSheets: NonNullable<typeof run.sheets> = [];
      const pages = st.withAgentMutation(() => prepared.map(({ url, label, cartoucheUrl, frame }) => {
        assertCurrent();
        const page = st.addPage(deck.projectId, {
          label,
          width: W,
          height: H,
          background: '#FFFFFF',
        });
        if (!page) throw new Error('SLIDES_PROPOSE_VISUAL: could not append the proposal page.');

        const imageId = slidesGenId(), cartoucheId = slidesGenId();
        assertCurrent();
        st.addElement(deck.projectId, page.id, {
          id: imageId,
          origin: 'agent',
          type: 'image',
          url,
          frame,
        } as SlideElement);
        assertCurrent();
        st.addElement(deck.projectId, page.id, {
          id: cartoucheId, origin: 'agent', type: 'image', url: cartoucheUrl,
          frame: fitInBox(2400, 280, layout.cartouche),
        } as SlideElement);
        recordedSheets.push({ pageId: page.id, imageId, imageUrl: url, cartoucheId, cartoucheUrl });
        return page;
      }), options?.isAgentGenerated);
      assertCurrent();
      st.recordProposalSheets(run, recordedSheets, croquis.verdict?.satisfied !== true || applied.verdict?.satisfied !== true);
      st.updateProposalProgress(run, { phase: 'finishing' });
      const proposalPage = pages[1]!;

      st.focusPage(deck.projectId, proposalPage.id);
      st.markAgentActive();

      if (!previousRun) await st.finishProposalRun(run, 'completed');

      // Hand both outcomes back. A draft the critic did not approve is still
      // placed - three attempts is where a pass stops, and the best of three
      // beats nothing on the page - but the agent must be able to SAY so rather
      // than presenting it as validated.
      return JSON.stringify({
        croquis: {
          attempts: croquis.attempts,
          stoppedReason: croquis.stoppedReason,
          approved: croquis.verdict?.satisfied === true,
          judged: croquis.verdict !== null,
          remainingProblems: croquis.verdict?.satisfied ? [] : (croquis.verdict?.problems ?? []),
        },
        proposal: {
          attempts: applied.attempts,
          stoppedReason: applied.stoppedReason,
          approved: applied.verdict?.satisfied === true,
          judged: applied.verdict !== null,
          remainingProblems: applied.verdict?.satisfied ? [] : (applied.verdict?.problems ?? []),
        },
        studioProjectId,
      });
      } catch (error) {
        if (!previousRun || options?.abortSignal?.aborted) {
          void st.finishProposalRun(run, options?.abortSignal?.aborted ? 'stopped' : 'failed');
        }
        throw error;
      } finally {
        options?.abortSignal?.removeEventListener('abort', stop);
      }
    },
  },

  SLIDES_DELETE_PAGE: {
    action_type: 'SLIDES_DELETE_PAGE',
    traceLabels: { active: 'deleting', done: 'deleted', unit: 'page', pluralUnit: 'pages' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Deletes a page from the open deck. Use it when the user asks to remove slides (e.g. "delete all pages except page 1" - call this once per page you must remove, highest page number first so the remaining numbering stays stable). A deck always keeps at least one page: the last remaining page cannot be deleted.',
    parameters: [
      { name: 'page', type: 'number', description: '1-based page number (or a page id string).', required: true },
    ],
    availableInModes: ['slides'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const deck = requireDeck('SLIDES_DELETE_PAGE', options?.projectId);
      const page = resolvePage(deck, params.page, 'SLIDES_DELETE_PAGE');
      if (deck.doc.pages.length <= 1) {
        throw new Error('SLIDES_DELETE_PAGE: the deck must keep at least one page.');
      }
      const st = useSlidesStore.getState();
      st.withAgentMutation(() => st.deletePage(deck.projectId, page.id), options?.isAgentGenerated);
      st.markAgentActive();
      toast.success(`Deleted slide ${page.index + 1}`);
    },
  },

  // A review asked for in the chat used to run inline: no cover page, no run, no progress, no Stop
  // or Continue (Sydney typed "launch review" on the HKM deck, 2026-10-09). This hands it to the button's path.
  SLIDES_LAUNCH_REVIEW: {
    action_type: 'SLIDES_LAUNCH_REVIEW',
    targetEntityType: 'ABSOLUTE',
    availableInModes: ['slides'],
    description:
      'Use when the user asks in the chat to start, launch, run, continue or resume the review of this deck. Never review from a chat request yourself: call this, say in one short sentence that the review is starting, and stop. When your turn ends the app starts the real review exactly like the Launch review / Continue review button (cover page, brand research if needed, progress bar, Stop and Continue).',
    parameters: [],
    handler: async (params, _kernel, options) => {
      const st = useSlidesStore.getState();
      const sessionId = options?.sessionId;
      const deck = st.activeProjectId ? st.decks[st.activeProjectId] : undefined;
      if (!sessionId || !deck) throw new Error('SLIDES_LAUNCH_REVIEW: open the deck first.');
      if (st.getAgentRun(sessionId)) throw new Error('SLIDES_LAUNCH_REVIEW: a review or counter-proposal is already running in this conversation; carry on with it.');
      if (reviewHasCompleteResult(deck)) throw new Error('SLIDES_LAUNCH_REVIEW: this review is complete. To start over, the user presses Review under the chat.');
      useSlidesStore.setState({ pendingReviewLaunch: { sessionId, projectId: deck.projectId } });
      params.result = { queued: true, mode: reviewProgress(deck).percent > 0 ? 'continue' : 'start', next: 'Say the review is starting, then stop: the app launches it when your turn ends.' };
    },
  },
  SLIDES_SET_REVIEW: {
    action_type: 'SLIDES_SET_REVIEW',
    traceLabels: { active: 'stamping', done: 'stamped', unit: 'verdict', pluralUnit: 'verdicts' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Sets the deck\'s review stamp: per-point verdicts (keyed p1..p10, each { verdict: pass|warn|fail|missing|na, comment }), the written synthesis, and the missing-pieces list. Idempotent and mergeable - set verdicts progressively as points conclude. Every verdict must rest on pages you have actually viewed.',
    parameters: [
      {
        name: 'points',
        type: 'object',
        description:
          'Partial map of point verdicts, e.g. {"p1": {"verdict": "fail", "comment": "Facade ~50% opaque in the first 3m - below the 75% see-through threshold."}}.',
        required: false,
      },
      { name: 'synthesis', type: 'string', description: 'The written synthesis (blocking points first).', required: false },
      { name: 'missingPieces', type: 'string[]', description: 'Named missing graphic pieces.', required: false },
      {
        name: 'header',
        type: 'object',
        description:
          'Validation-card header, extracted from the proposal: {"cellule":"ONLY","centre":"Westfield Euralille"}. cellule = the retailer/brand, centre = the mall. The RDM box holds the reviewer\'s own initials, filled by the app: never send rdm.',
        required: false,
      },
      {
        name: 'status',
        type: 'string',
        description:
          "Overall design verdict: 'A' (validé, 10/10), 'B' (validé sous réserves), or 'C' (non validé - a structural point failed / forbidden material / incomplete file).",
        required: false,
      },
      {
        name: 'authorityStatus',
        type: 'string',
        description:
          "'pass' or 'fail': whether the dossier can be filed with the authorities. INDEPENDENT of `status` - a scheme can be filable while still carrying design reservations. Leave unset to derive it from status as before.",
        required: false,
      },
      {
        name: 'language',
        type: 'string',
        description:
          "'fr' or 'en': the language of the deliverable's fixed captions (the validation card). The deliverable must be entirely one language so it can be handed to the tenant as it is. Set it to match the language you are writing the comments in.",
        required: false,
      },
    ],
    availableInModes: ['slides'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const deck = requireDeck('SLIDES_SET_REVIEW', options?.projectId);
      const patch: {
        points?: Record<string, DeckReviewPointState>;
        synthesis?: string;
        missingPieces?: string[];
        header?: { rdm?: string; cellule?: string; centre?: string };
        status?: 'A' | 'B' | 'C';
        daNote?: string;
        authorityStatus?: 'pass' | 'fail';
        language?: 'en' | 'fr';
      } = {};
      if (params.points && typeof params.points === 'object') {
        const points: Record<string, DeckReviewPointState> = {};
        for (const [pid, raw] of Object.entries(params.points as Record<string, unknown>)) {
          if (!REVIEW_POINT_IDS.has(pid) || !raw || typeof raw !== 'object') continue;
          const entry = raw as Record<string, unknown>;
          const verdict = entry.verdict as SlideReviewVerdict;
          if (!VALID_VERDICTS.has(verdict)) {
            throw new Error(`SLIDES_SET_REVIEW: invalid verdict "${String(entry.verdict)}" for ${pid}.`);
          }
          points[pid] = {
            verdict,
            comment: typeof entry.comment === 'string' ? entry.comment : undefined,
          };
        }
        if (Object.keys(points).length > 0) patch.points = points;
      }
      if (typeof params.synthesis === 'string' && params.synthesis.trim()) patch.synthesis = params.synthesis.trim();
      if (Array.isArray(params.missingPieces)) {
        patch.missingPieces = params.missingPieces.filter((p: unknown): p is string => typeof p === 'string' && !!p.trim());
      }
      if (params.header && typeof params.header === 'object') {
        const h = params.header as Record<string, unknown>;
        const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
        // RDM is the reviewer, not something in the tenant's file: the agent wrote "FR" over "YD" on
        // a live run (2026-10-06). Only the keys it actually sends, so a missing one cannot erase a value.
        const header = Object.fromEntries(Object.entries({ cellule: str(h.cellule), centre: str(h.centre) }).filter(([, v]) => v));
        if (Object.keys(header).length) patch.header = header;
      }
      if (params.status === 'A' || params.status === 'B' || params.status === 'C') patch.status = params.status;
      else if (params.status !== undefined) {
        throw new Error(`SLIDES_SET_REVIEW: invalid status "${String(params.status)}" (expected 'A', 'B' or 'C').`);
      }
      if (params.authorityStatus === 'pass' || params.authorityStatus === 'fail') {
        patch.authorityStatus = params.authorityStatus;
      } else if (params.authorityStatus !== undefined) {
        throw new Error(
          `SLIDES_SET_REVIEW: invalid authorityStatus "${String(params.authorityStatus)}" (expected 'pass' or 'fail').`
        );
      }
      if (params.language === 'fr' || params.language === 'en') patch.language = params.language;
      else if (params.language !== undefined) {
        throw new Error(`SLIDES_SET_REVIEW: invalid language "${String(params.language)}" (expected 'fr' or 'en').`);
      }
      if (
        !patch.points && patch.synthesis === undefined && patch.missingPieces === undefined &&
        patch.header === undefined && patch.status === undefined &&
        patch.authorityStatus === undefined && patch.language === undefined
      ) {
        throw new Error('SLIDES_SET_REVIEW: provide at least one of points / synthesis / missingPieces / header / status / authorityStatus / language.');
      }
      const st = useSlidesStore.getState();
      st.withAgentMutation(() => {
        st.setReview(deck.projectId, patch);
        if (patch.points || patch.synthesis) st.setDeckMeta(deck.projectId, { status: 'REVIEWING' });
      }, options?.isAgentGenerated);
      st.markAgentActive();
      const count = patch.points ? Object.keys(patch.points).length : 0;
      if (count > 0) toast.success(`Review stamp updated (${count} verdict${count === 1 ? '' : 's'})`);
    },
  },

  SLIDES_MOVE_PAGE: {
    action_type: 'SLIDES_MOVE_PAGE',
    traceLabels: { active: 'reordering', done: 'reordered', unit: 'page' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Reorders a page within the deck (the user can do this by dragging in the slide rail). Positions are 1-based and refer to the deck AFTER the move, so moving page 5 to position 1 makes it the first slide.',
    parameters: [
      { name: 'page', type: 'number', description: '1-based page number (or a page id string) to move.', required: true },
      { name: 'toPosition', type: 'number', description: '1-based destination position.', required: true },
    ],
    availableInModes: ['slides'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const deck = requireDeck('SLIDES_MOVE_PAGE', options?.projectId);
      const page = resolvePage(deck, params.page, 'SLIDES_MOVE_PAGE');
      const to = typeof params.toPosition === 'number' ? Math.round(params.toPosition) : NaN;
      if (!Number.isFinite(to) || to < 1 || to > deck.doc.pages.length) {
        throw new Error(
          `SLIDES_MOVE_PAGE: "toPosition" must be between 1 and ${deck.doc.pages.length}.`
        );
      }
      const st = useSlidesStore.getState();
      st.withAgentMutation(() => st.movePage(deck.projectId, page.id, to - 1), options?.isAgentGenerated);
      st.markAgentActive();
    },
  },

  SLIDES_DUPLICATE_PAGE: {
    action_type: 'SLIDES_DUPLICATE_PAGE',
    traceLabels: { active: 'duplicating', done: 'duplicated', unit: 'page' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Duplicates a page (all of its elements, with fresh ids) and inserts the copy directly after the original. Use it to iterate on a layout without losing the version you started from.',
    parameters: [
      { name: 'page', type: 'number', description: '1-based page number (or a page id string).', required: true },
    ],
    availableInModes: ['slides'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const deck = requireDeck('SLIDES_DUPLICATE_PAGE', options?.projectId);
      const page = resolvePage(deck, params.page, 'SLIDES_DUPLICATE_PAGE');
      const st = useSlidesStore.getState();
      const copy = st.withAgentMutation(() => st.duplicatePage(deck.projectId, page.id), options?.isAgentGenerated);
      if (!copy) throw new Error('SLIDES_DUPLICATE_PAGE: could not duplicate that page.');
      st.focusPage(deck.projectId, copy.id);
      st.markAgentActive();
    },
  },

  SLIDES_SET_PAGE_PROPS: {
    action_type: 'SLIDES_SET_PAGE_PROPS',
    traceLabels: { active: 'formatting', done: 'formatted', unit: 'page' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Sets page-level properties: "background" (hex fill for the whole slide - this is the real page background, not a full-bleed patch element), and the page SIZE in points via either a "preset" ("16:9" = 960x540, "4:3" = 960x720, "A4_landscape" = 842x595, "A4_portrait" = 595x842, "letter_landscape" = 792x612) or explicit width/height. Resizing does NOT reflow existing elements - their point coordinates are unchanged.',
    parameters: [
      { name: 'page', type: 'number', description: '1-based page number (or a page id string).', required: true },
      { name: 'background', type: 'string', description: 'Hex page background, e.g. "#0B0B0B".', required: false },
      {
        name: 'preset',
        type: 'string',
        description: 'One of 16:9 | 4:3 | A4_landscape | A4_portrait | letter_landscape.',
        required: false,
      },
      { name: 'width', type: 'number', description: 'Explicit page width in points.', required: false },
      { name: 'height', type: 'number', description: 'Explicit page height in points.', required: false },
    ],
    availableInModes: ['slides'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const deck = requireDeck('SLIDES_SET_PAGE_PROPS', options?.projectId);
      const page = resolvePage(deck, params.page, 'SLIDES_SET_PAGE_PROPS');
      const patch: { background?: string; width?: number; height?: number } = {};

      if (params.background !== undefined) {
        const bg = typeof params.background === 'string' ? params.background.trim() : '';
        if (!/^#?[0-9a-fA-F]{3,8}$/.test(bg)) {
          throw new Error('SLIDES_SET_PAGE_PROPS: "background" must be a hex colour like "#FFFFFF".');
        }
        patch.background = bg;
      }
      if (params.preset !== undefined) {
        const size = PAGE_SIZE_PRESETS[String(params.preset)];
        if (!size) {
          throw new Error(
            `SLIDES_SET_PAGE_PROPS: unknown preset "${String(params.preset)}". Use ${Object.keys(PAGE_SIZE_PRESETS).join(' | ')}.`
          );
        }
        patch.width = size.width;
        patch.height = size.height;
      }
      if (typeof params.width === 'number' && params.width > 0) patch.width = params.width;
      if (typeof params.height === 'number' && params.height > 0) patch.height = params.height;

      if (Object.keys(patch).length === 0) {
        throw new Error('SLIDES_SET_PAGE_PROPS: provide background, preset, or width/height.');
      }
      const st = useSlidesStore.getState();
      st.withAgentMutation(() => st.patchPage(deck.projectId, page.id, patch), options?.isAgentGenerated);
      st.markAgentActive();
    },
  },

  SLIDES_REORDER_ELEMENT: {
    action_type: 'SLIDES_REORDER_ELEMENT',
    traceLabels: { active: 'restacking', done: 'restacked', unit: 'element' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Changes an element\'s stacking order on its page. Elements paint in list order - later elements draw ON TOP - in the editor, the slide rail and the exported PDF alike. Use this when a patch or image covers annotations that should stay visible.',
    parameters: [
      { name: 'page', type: 'number', description: '1-based page number (or a page id string).', required: true },
      { name: 'elementId', type: 'string', description: 'The id of the element to restack.', required: true },
      {
        name: 'to',
        type: 'string',
        description: 'One of front | back | forward | backward.',
        required: true,
      },
    ],
    availableInModes: ['slides'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const deck = requireDeck('SLIDES_REORDER_ELEMENT', options?.projectId);
      const page = resolvePage(deck, params.page, 'SLIDES_REORDER_ELEMENT');
      const elementId = typeof params.elementId === 'string' ? params.elementId.trim() : '';
      if (!elementId) throw new Error('SLIDES_REORDER_ELEMENT: "elementId" is required.');
      const to = String(params.to);
      if (!['front', 'back', 'forward', 'backward'].includes(to)) {
        throw new Error('SLIDES_REORDER_ELEMENT: "to" must be front | back | forward | backward.');
      }
      const st = useSlidesStore.getState();
      st.withAgentMutation(() => st.reorderElement(deck.projectId, page.id, elementId, to as 'front' | 'back' | 'forward' | 'backward'), options?.isAgentGenerated);
      st.markAgentActive();
    },
  },

  SLIDES_DUPLICATE_ELEMENT: {
    action_type: 'SLIDES_DUPLICATE_ELEMENT',
    traceLabels: { active: 'duplicating', done: 'duplicated', unit: 'element' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Copies an element on its page (fresh id, offset slightly so the copy is visible). Faithful for long text - cloning by hand from your context would truncate it.',
    parameters: [
      { name: 'page', type: 'number', description: '1-based page number (or a page id string).', required: true },
      { name: 'elementId', type: 'string', description: 'The id of the element to copy.', required: true },
    ],
    availableInModes: ['slides'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const deck = requireDeck('SLIDES_DUPLICATE_ELEMENT', options?.projectId);
      const page = resolvePage(deck, params.page, 'SLIDES_DUPLICATE_ELEMENT');
      const elementId = typeof params.elementId === 'string' ? params.elementId.trim() : '';
      if (!elementId) throw new Error('SLIDES_DUPLICATE_ELEMENT: "elementId" is required.');
      const st = useSlidesStore.getState();
      const copy = st.withAgentMutation(() => st.duplicateElement(deck.projectId, page.id, elementId), options?.isAgentGenerated);
      if (!copy) throw new Error(`SLIDES_DUPLICATE_ELEMENT: unknown element "${elementId}".`);
      st.markAgentActive();
    },
  },

  SLIDES_REMOVE_ELEMENTS: {
    action_type: 'SLIDES_REMOVE_ELEMENTS',
    traceLabels: { active: 'clearing', done: 'cleared', unit: 'element', pluralUnit: 'elements' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Removes several elements from a page in one call. Give explicit "elementIds", or a filter: "guidelinePointId" (every annotation for that point) or "elementType". With no filter and no ids, clears every element on the page. This is the round-2 tool - clearing 20 annotations one at a time costs 20 commands. A filtered or whole-page clear that matches nothing SUCCEEDS quietly (the page is already in the state you asked for); only explicit ids that do not exist are an error, because those mean you are working from stale ids.',
    parameters: [
      { name: 'page', type: 'number', description: '1-based page number (or a page id string).', required: true },
      { name: 'elementIds', type: 'string[]', description: 'Explicit element ids to remove.', required: false },
      {
        name: 'guidelinePointId',
        type: 'string',
        description: 'Remove every element tagged with this review point (p1..p10).',
        required: false,
      },
      {
        name: 'elementType',
        type: 'string',
        description: 'Remove every element of this type (note|arrow|rect|ellipse|text|stamp|image|patch).',
        required: false,
      },
    ],
    availableInModes: ['slides'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const deck = requireDeck('SLIDES_REMOVE_ELEMENTS', options?.projectId);
      const page = resolvePage(deck, params.page, 'SLIDES_REMOVE_ELEMENTS');
      const explicit = Array.isArray(params.elementIds)
        ? params.elementIds.filter((v): v is string => typeof v === 'string' && !!v.trim()).map((v) => v.trim())
        : [];
      const pointId = typeof params.guidelinePointId === 'string' ? params.guidelinePointId.trim() : '';
      const elType = typeof params.elementType === 'string' ? params.elementType.trim() : '';
      if (elType && !VALID_ELEMENT_TYPES.has(elType)) {
        throw new Error(`SLIDES_REMOVE_ELEMENTS: unknown elementType "${elType}".`);
      }

      let ids: string[];
      if (explicit.length > 0) {
        ids = explicit;
      } else if (pointId || elType) {
        ids = page.elements
          .filter((e) => (!pointId || e.guidelinePointId === pointId) && (!elType || e.type === elType))
          .map((e) => e.id);
      } else {
        ids = page.elements.map((e) => e.id);
      }
      // "Nothing matched" means two different things, and treating them alike
      // was wrong in both directions.
      //
      // A CLEANUP ("clear page 1", "remove every p3 annotation") that finds
      // nothing has already achieved what it asked for. Throwing there failed
      // the agent's script on a deck that was simply already clean - which is
      // exactly what happens now that the reviewer can wipe a review from the
      // composer before asking for a new one.
      // Explicit ids are copied verbatim into `ids`, so an empty `ids` here can
      // only be a filtered or whole-page clear. Stale explicit ids are caught
      // after the store call, on `removed === 0`.
      if (ids.length === 0) {
        console.info(`[Slides] SLIDES_REMOVE_ELEMENTS: page ${page.index + 1} already had nothing to clear.`);
        return;
      }

      const st = useSlidesStore.getState();
      const removed = st.withAgentMutation(() => st.removeElements(deck.projectId, page.id, ids), options?.isAgentGenerated);
      // Explicit ids that matched nothing is the OTHER half: it used to pass
      // silently with "Removed 0 elements", so an agent working from stale ids
      // was told it had cleaned up when it had not.
      if (removed === 0 && explicit.length > 0) {
        throw new Error(
          `SLIDES_REMOVE_ELEMENTS: none of the ${explicit.length} element id(s) you gave exist on page ${page.index + 1}. Re-read the page's elements from your deck context - the ids you used are stale.`
        );
      }
      st.markAgentActive();
      toast.success(`Removed ${removed} element${removed === 1 ? '' : 's'}`);
    },
  },

  SLIDES_SET_DECK_META: {
    action_type: 'SLIDES_SET_DECK_META',
    traceLabels: { active: 'updating', done: 'updated', unit: 'deck' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Sets deck-level metadata: "guidelineKey" (which guideline the deck is judged against), "status" (DRAFT | REVIEWING | REVIEWED - set REVIEWED when you close a review), "reviewRound" (bump to 2 when the taker resubmits and you start a diff review), and "name" (the deck\'s display name).',
    parameters: [
      { name: 'guidelineKey', type: 'string', description: 'Guideline key, e.g. "inline_unit".', required: false },
      { name: 'status', type: 'string', description: 'DRAFT | REVIEWING | REVIEWED.', required: false },
      { name: 'reviewRound', type: 'number', description: 'Review round (1, 2, …).', required: false },
      { name: 'name', type: 'string', description: 'Deck display name.', required: false },
      ],
    availableInModes: ['slides'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const deck = requireDeck('SLIDES_SET_DECK_META', options?.projectId);
      const patch: Parameters<ReturnType<typeof useSlidesStore.getState>['setDeckMeta']>[1] = {};
      if (params.guidelineKey !== undefined) {
        patch.guidelineKey = String(params.guidelineKey) as SlideDeckDTO['guidelineKey'];
      }
      if (params.status !== undefined) {
        const status = String(params.status).toUpperCase();
        if (!['DRAFT', 'REVIEWING', 'REVIEWED'].includes(status)) {
          throw new Error('SLIDES_SET_DECK_META: "status" must be DRAFT | REVIEWING | REVIEWED.');
        }
        patch.status = status as SlideDeckDTO['status'];
      }
      if (params.reviewRound !== undefined) {
        const round = Number(params.reviewRound);
        if (!Number.isFinite(round) || round < 1) {
          throw new Error('SLIDES_SET_DECK_META: "reviewRound" must be 1 or more.');
        }
        patch.reviewRound = Math.round(round);
      }
      if (params.name !== undefined) patch.sourceFileName = String(params.name);
      if (Object.keys(patch).length === 0) {
        throw new Error('SLIDES_SET_DECK_META: provide at least one field to change.');
      }
      const st = useSlidesStore.getState();
      st.withAgentMutation(() => st.setDeckMeta(deck.projectId, patch), options?.isAgentGenerated);
      st.markAgentActive();
    },
  },
};

export const slidesCommandTimeEstimations: Record<string, number> = {
  SLIDES_READ_PAGE: 3,
  SLIDES_GOTO_PAGE: 1,
  SLIDES_ADD_ELEMENT: 4,
  SLIDES_UPDATE_ELEMENT: 2,
  SLIDES_REMOVE_ELEMENT: 1,
  SLIDES_UPDATE_PAGE: 1,
  SLIDES_ADD_PAGE: 2,
  SLIDES_DELETE_PAGE: 1,
  SLIDES_SET_REVIEW: 4,
  SLIDES_MOVE_PAGE: 1,
  SLIDES_DUPLICATE_PAGE: 2,
  SLIDES_SET_PAGE_PROPS: 1,
  SLIDES_REORDER_ELEMENT: 1,
  SLIDES_DUPLICATE_ELEMENT: 1,
  SLIDES_REMOVE_ELEMENTS: 2,
  SLIDES_SET_DECK_META: 1,
};

// Dev-only handle so the E2E harness can execute REAL agent commands (same
// handlers the LLM runs) instead of only exercising the store beneath them.
// Never attached in a production build.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  (window as unknown as { __slidesCommands?: typeof SLIDES_COMMAND_DEFINITIONS }).__slidesCommands =
    SLIDES_COMMAND_DEFINITIONS;
}
